import * as Yup from "yup";
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown } from "lucide-react";
import {
  Button,
  ChipInput,
  CustomSelect,
  TextArea,
  TextInput,
  Tooltip,
  AIAssistButtons,
  toast,
  InputWithMediaInfo,
  SelectedMediaCard,
} from "../..";
import { useFormik } from "formik";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getListMedia,
  getSearchedListMedia,
  getVodDetails,
  updateVideoPublishMedia,
  getListChapters,
} from "@/store/media/media.action";
import type { RootState } from "../../../store";
import { secondsToHms } from "../../../utils";
import { generateBaseUrl } from "@/utils/mediaUtils";
import type { InputWithMediaInfoRef } from "@/database";
import {
  formatTimestampToAgo,
  socialPublishStatusToTextAndColor,
  ButtonText,
  openVideoInNewTab,
  socialPublishStatusTooltipText,
  CONST_SOCIAL_THUMB_SIZE,
  scrollToFirstError,
} from "../../../utils";
import ThumbnailSelection from "../../ThumbnailSelection/ThumbnailSelection";
import { GeneralPlayer } from "../../CleoPlayer";
import type { GeneralPlayerHandle, ImageReel } from "../../CleoPlayer";
import VideoRangeTrimmer from "../../VideoRangeTrimmer/VideoRangeTrimmer";
import HourglassIcon from "../../../assets/icons/svg/hourglass.svg?react";
import { UnifiedDateTimePicker } from "../../DateInput/UnifiedDateTimePicker";
import { aiService } from "@/store/app/app.actions";
import { store } from "../../../store";
import noImage from "../../../assets/icons/svg/no-image.svg";
import GraphemeSplitter from "grapheme-splitter";
import { addToHistory, handleHistoryNavigation } from "../../../utils";
import AiTransformer from "../../Transformer/AiTransformer";
import {
  checkYoutubeThumbnailDimensions,
  getThumbnailUrlUtil,
} from "@/utils/mediaUtils";
import {
  AssetItem,
  ConditionalRules,
  PrePost,
  SocialChannel,
  ChapterInterface,
} from "@/database";
import { PlatformConfig, getPlatformConfig } from "./platformConfig";
import { AlertDialog } from "@/utils/ui/AlertDialog/AlertDialog";
import MediaDelete from "../../MediaList/MediaDelete";
import {
  editedFieldsToArray,
  buildChannelPublishHistory,
  resolveNextPublishMode,
  segmentOverlapsHistory,
  SEGMENT_BUFFER_SECONDS,
  htmlTextToPlain,
} from "./utils";
import DailymotionIcon from "../../../assets/icons/svg/dailymotion-icon.svg?react";
import {
  PRESET_MODES,
  SOCIAL_PUBLISH_STATUS,
  DEFAULT_YOUTUBE_CATEGORIES,
  PUBLISH_RANGE_TYPE,
  type PublishRangeType,
} from "../../../constants";
import {
  resolveTagsByMode,
  resolveThumbIdByOrientation,
} from "@/utils/mediautils/CommonFunctions";
import { GetMonetizationOptionsAPI } from "@/database";
import { MonetizationOptions, YoutubePlaylist } from "@/database";
import { MediaModel } from "@/database";
import { useThumbnailSelection } from "./hooks/useThumbnailSelection";
import { usePublishCardAiFields } from "./hooks/usePublishCardAiFields";
import { setMediaDetails } from "@/store/media/media.slice";
import { Alert } from "@/utils/ui/Alert/Alert";
import {
  areYoutubeSocialTagsCharLengthValid,
  calculateTagCount,
  YOUTUBE_SOCIAL_TAG_LENGTH_ERROR_MESSAGE,
} from "@/utils/mediautils/CommonFunctions";
import CustomMetaRules from "./CustomMetaRules";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";

const splitter = new GraphemeSplitter();
const countGraphemes = (str: string) => splitter.countGraphemes(str);

// Module-level cache to track fetching state across all component instances
// This prevents duplicate API calls when multiple cards have the same channel_id
const fetchingCache = {
  playlist: new Set<string>(),
  chapters: new Set<string>(),
};

const inFlightMonetization = new Map<
  string,
  Promise<MonetizationOptions[] | null>
>();

const rotatingStyles = `
  @keyframes rotate {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(0deg); }
  }
  .rotating-hourglass {
    animation: rotate 2s infinite linear;
    display: inline-block;
    width: 16px;
    height: 16px;
  }
`;

/**
 * Notifies the parent when a destination publishes, so it can flip the parent publish entry
 * (clearing the Save & Publish guard / showing failed). info.auto marks the optimistic
 * playlist-driven synthetic path; otherwise it carries the real backend {status, msg}.
 * info.updated marks the Edit -> Sync -> Update flow on an already-published
 * destination, so the caller can show "Updated." instead of "Published."
 * info.saved marks a first-time submit made via the Save button (staged as
 * DRAFT rather than queued), so the caller can show "Saved." instead of "Published."
 */
export type OnPublished = (
  id: string,
  info: {
    auto?: boolean;
    status?: number;
    msg?: string;
    updated?: boolean;
    saved?: boolean;
  }
) => void;

const composeAffixed = (base: string, prefix: string, suffix: string) => {
  const b = (base ?? "").trim();
  const p = (prefix ?? "").trim();
  const s = (suffix ?? "").trim();
  const needsPrefix = p && !b.startsWith(p);
  const needsSuffix = s && !b.endsWith(s);
  return [needsPrefix ? p : "", b, needsSuffix ? s : ""]
    .filter(Boolean)
    .join(" ");
};

export interface BasePublishCardProps {
  initialdata: any;
  assets: AssetItem[];
  disabled: boolean;
  handleCancelOrDelete: (
    initialdata: any,
    type: string,
    cardType: string
  ) => void | Promise<void>;
  callPublishMedia: (payload: any, cb: (response: any) => void) => void;
  updatePublishMedia: (payload: any, cb: (response: any) => void) => void;
  isPublishingMedia: boolean;
  mediaBaseUrl: string;
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  updateModal: any;
  isWizard: boolean;
  currentMediaDetails: MediaModel;
  isPreset?: boolean;
  handleAllSelectedPresets?: (presets: any[]) => void;
  item?: any;
  presetDetails?: any;
  isPreview?: boolean;
  readOnlyMode?: boolean;
  metaPresetData?: any;
  // Platform-specific props
  getYoutubePlaylist?: any;
  youtubePlaylist?: YoutubePlaylist[];
  platform?: string;
  bunchLanguageList?: any[];
  socialChannelList?: SocialChannel[];
  /** Rendered inside the media-edit Offcanvas sheet: scoped layout (paired fields, no card chrome). */
  inSheet?: boolean;
  onPublished?: OnPublished;
  onStageDraft?: (
    id: string,
    payload: any,
    opts?: { silent?: boolean }
  ) => void;
  autoStage?: boolean;
  /**
   * Notifies the parent that the user manually edited one of the synced fields.
   * The parent persists this on the publish object so the "stop syncing this
   * field" flag survives the card unmounting when the sheet closes.
   */
  onFieldEdited?: (
    id: string,
    field: "title" | "desc" | "tags" | "thumbnail"
  ) => void;
  /**
   * Persists the current values of edited fields onto the publish object so they
   * survive the card unmount/remount when the sheet closes (formik re-seeds from
   * initialdata on remount).
   */
  onPersistEditedValues?: (id: string, patch: Record<string, any>) => void;
  /** Notifies the parent that the user saved this card (freezes all its fields). */
  onCardSaved?: (id: string) => void;
  // Custom render functions for platform-specific fields
  renderPlatformSpecificFields?: (
    formik: any,
    config: PlatformConfig,
    enabledFields: Record<string, boolean>,
    handleFieldToggle: (fieldName: string, checked: boolean) => void,
    monetizationOptions?: MonetizationOptions[],
    youtubePlaylist?: YoutubePlaylist[],
    isSftp?: boolean,
    disabled?: boolean,
    lockedFieldsDisabled?: boolean
  ) => React.ReactNode;
}

interface ClipFieldValues {
  title: string;
  desc: string;
  tags: string[];
  category: string;
  playlist: string;
  is_created_for_kids: string;
  privacy: string;
  thumb: string;
  thumbId: string;
  monetize_policy_id: string;
  monetize_policy_name: string;
  is_rights_managements_allowed: string;
}

interface BaseFormValues {
  id: string;
  platform: string;
  channel_name: string;
  title: string;
  desc: string;
  tags: string[];
  thumb: string;
  thumbId: string;
  status: number;
  msg: string;
  privacy: string;
  job_type: string;
  upload_type: string;
  media_id: string;
  ct: number;
  mt: number;
  isNew?: number;
  title_ai_type?: string;
  title_ai_value?: string;
  desc_ai_type?: string;
  desc_ai_value?: string;
  title_ai_checked?: boolean;
  desc_ai_checked?: boolean;
  schedule_after?: number;
  schedule?: number;
  // YouTube specific
  category?: string;
  monetize_policy_id?: string;
  monetize_policy_name?: string;
  playlist?: string;
  is_created_for_kids?: string;
  is_rights_managements_allowed?: string;
  // Facebook specific
  pageName?: string;
  image?: string;
  isChecked?: boolean;
  conditional_rules?: ConditionalRules[];
  preVideo?: string;
  postVideo?: string;
}

const BasePublishCard = ({
  initialdata,
  assets,
  disabled: disabledProp = false,
  handleCancelOrDelete,
  callPublishMedia,
  updatePublishMedia,
  mediaBaseUrl,
  expandedId,
  onToggleExpand,
  updateModal,
  isWizard,
  currentMediaDetails,
  isPreset = false,
  handleAllSelectedPresets,
  item,
  presetDetails,
  isPreview = false,
  readOnlyMode = false,
  metaPresetData,
  getYoutubePlaylist,
  youtubePlaylist,
  platform,
  bunchLanguageList,
  renderPlatformSpecificFields,
  socialChannelList,
  inSheet = false,
  onPublished,
  onStageDraft,
  autoStage = false,
  onFieldEdited,
  onPersistEditedValues,
  onCardSaved,
}: BasePublishCardProps) => {
  // Published media (status 10) is normally locked (disabledProp === true).
  // Edit -> Sync Changes -> Update unlocks it in place of the disabled Save/Publish pair.
  const isPublishedMedia = initialdata?.status === 10;
  const [publishEditStage, setPublishEditStage] = useState<
    "edit" | "sync" | "update"
  >("edit");
  const disabled = isPublishedMedia
    ? publishEditStage !== "update"
    : disabledProp;
  // A handful of fields are never touched by the Edit -> Sync -> Update flow
  // on already-published media - unlike the rest of the form (title/desc/
  // tags/etc, which `disabled` correctly unlocks once publishEditStage
  // reaches "update"), these must stay locked through every stage of that
  // flow, not just the initial "edit" one: the slate edit/delete buttons,
  // YouTube's rights management/created-for-kids/monetize fields (via
  // renderPlatformSpecificFields/YoutubePublishCard's lockedFieldsDisabled
  // param - Category/Playlist there still use the normal `disabled`), and
  // LinkedIn/Facebook's visibility field below.
  const lockedOnPublished = disabled || isPublishedMedia;
  // Directly editing an existing clips-type record (opened via its own
  // pencil/edit, not a fresh repeat-attempt draft) is scoped to that one
  // clip's metadata - it was already submitted as a single-clip publish, so
  // switching mode away from Clips (or picking a different clip) isn't
  // supported here.
  const isExistingClipRecord =
    initialdata?.isNew !== 100 &&
    initialdata?.publish_type === PUBLISH_RANGE_TYPE.CLIPS &&
    !!initialdata?.publish_clip_id;
  // Snapshot of form values taken the moment sync unlocks the form — comparing
  // against formik.initialValues would false-positive: several effects in this
  // component (thumb/slate/AI defaults) call setFieldValue on mount, which marks
  // formik.dirty true before the user ever touches anything.
  const publishSyncSnapshotRef = useRef<string | null>(null);
  // Clips mode edits the active clip's title/desc/tags via clipFieldValues,
  // a separate state map from formik.values (see the per-clip tab editor
  // below) - hasChangesSincePublishSync must snapshot and compare this too,
  // or editing a clip's fields after Sync never registers as a change and
  // the Update button stays disabled no matter what's typed.
  const clipFieldValuesSyncSnapshotRef = useRef<string | null>(null);
  // Set right before handlePublish() is called from the Edit -> Sync -> Update
  // flow, so submitValues can tell this submit apart from a draft/new-channel
  // publish even after setPublishEditStage("edit") has already fired — that
  // state update lands before formik's async validateForm().then() resolves,
  // so publishEditStage itself is unreliable to read inside submitValues.
  const isVideoUpdateSubmitRef = useRef(false);
  // True while the Update button's socialpublish.update_video call is in
  // flight - drives the button's "Updating..." spinner and keeps the sheet
  // open until the RPC actually resolves, instead of optimistically closing
  // it the instant the button is clicked.
  const [isUpdatingVideo, setIsUpdatingVideo] = useState(false);
  const [showVisibilityDowngradeConfirm, setShowVisibilityDowngradeConfirm] =
    useState(false);
  const [showPublishedDeleteConfirm, setShowPublishedDeleteConfirm] =
    useState(false);

  // Edit -> (auto) Sync Changes -> Update, gating on isPublishedMedia only.
  const handlePublishEditFlowClick = () => {
    if (publishEditStage === "edit") {
      setPublishEditStage("sync");

      console.log("this is the initialdata", initialdata);
      const videoId = initialdata?.routing_id;
      if (videoId) {
        dispatch(
          getVodDetails({ video_id: videoId }, (response: any) => {
            const vodDetails = response?.data?.vod_details;
            if (response?.status === 200 && vodDetails) {
              if (vodDetails.title != null) {
                formik.setFieldValue("title", vodDetails.title);
              }
              if (vodDetails.description != null) {
                formik.setFieldValue(
                  "desc",
                  htmlTextToPlain(vodDetails.description)
                );
              }
              if (vodDetails.category != null) {
                formik.setFieldValue("category", vodDetails.category);
              }
              if (vodDetails.playlist != null) {
                formik.setFieldValue("playlist", vodDetails.playlist);
              }
              if (vodDetails.privacy != null) {
                formik.setFieldValue("privacy", vodDetails.privacy);
              }
              if (Array.isArray(vodDetails.tags)) {
                formik.setFieldValue("tags", vodDetails.tags);
              }
              // if (vodDetails.thumb != null) {
              //   formik.setFieldValue("thumb", vodDetails.thumb);
              // }
              // Clips mode edits via clipFieldValues, not formik.values -
              // sync the same clip's entry so the editor shows the freshly
              // synced content, and snapshot the resulting map (computed
              // synchronously here, since setClipFieldValues won't have
              // applied yet) so hasChangesSincePublishSync compares against
              // the right baseline.
              let nextClipFieldValues = clipFieldValues;
              if (isExistingClipRecord && initialdata?.publish_clip_id) {
                const clipId = initialdata.publish_clip_id as string;
                const current = clipFieldValues[clipId];
                if (current) {
                  nextClipFieldValues = {
                    ...clipFieldValues,
                    [clipId]: {
                      ...current,
                      ...(vodDetails.title != null
                        ? { title: vodDetails.title }
                        : {}),
                      ...(vodDetails.description != null
                        ? { desc: htmlTextToPlain(vodDetails.description) }
                        : {}),
                      ...(Array.isArray(vodDetails.tags)
                        ? { tags: vodDetails.tags }
                        : {}),
                      ...(vodDetails.privacy != null
                        ? { privacy: vodDetails.privacy }
                        : {}),
                    },
                  };
                  setClipFieldValues(nextClipFieldValues);
                }
              }
              publishSyncSnapshotRef.current = JSON.stringify(formik.values);
              clipFieldValuesSyncSnapshotRef.current =
                JSON.stringify(nextClipFieldValues);
              setPublishEditStage("update");
            } else {
              // Sync failed - drop back to the locked "Edit" state (not
              // "update") so the form stays disabled and the button reads
              // "Edit" again, letting the user retry the sync instead of
              // silently landing in an unlocked/editable state on stale data.
              // getVodDetails already raised the failure toast.
              setPublishEditStage("edit");
            }
          }) as any
        );
      } else {
        window.setTimeout(() => {
          publishSyncSnapshotRef.current = JSON.stringify(formik.values);
          clipFieldValuesSyncSnapshotRef.current =
            JSON.stringify(clipFieldValues);
          setPublishEditStage("update");
        }, 900);
      }
      return;
    }
    if (isVisibilityDowngradedSinceSync) {
      setShowVisibilityDowngradeConfirm(true);
      return;
    }
    isVideoUpdateSubmitRef.current = true;
    setIsUpdatingVideo(true);
    handlePublish();
  };

  const confirmVisibilityDowngradeAndPublish = () => {
    isVideoUpdateSubmitRef.current = true;
    setIsUpdatingVideo(true);
    handlePublish();
  };

  const platformType = platform || initialdata?.job_type || "linkedin";
  const config = getPlatformConfig(platformType);

  // Clicking an already-published card previously just opened the sheet on
  // the locked "Edit" stage, requiring a second, separate click on the
  // in-sheet Edit button before Sync Changes actually started - kick the
  // same flow off automatically the moment this card's sheet/expanded view
  // becomes visible, so opening it is the only click needed. Only fires once
  // per open (isPublishedMedia && stage still "edit"); once the flow itself
  // advances the stage (sync/update, or back to edit on a failed sync), this
  // effect no longer matches and won't re-trigger on its own.
  // The embedded flow (MediaDetailsForm's "SOCIAL PUBLISH" sidebar chips,
  // identifiable by onStageDraft being passed - see renderPublishCard in
  // SocialPublish.tsx) opts out entirely - opening one of those chips must
  // not auto-trigger Sync, only the standalone Social-tab page's card click
  // should. Instagram never supports Sync/Update at all (see the disabled
  // Edit/Sync/Update button below, gated on platformType === "instagram"),
  // so it's excluded here the same way.
  useEffect(() => {
    const isOpen = inSheet || expandedId === initialdata.id;
    if (
      isOpen &&
      isPublishedMedia &&
      !onStageDraft &&
      platformType !== "instagram" &&
      publishEditStage === "edit"
    ) {
      handlePublishEditFlowClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inSheet,
    expandedId,
    initialdata.id,
    isPublishedMedia,
    onStageDraft,
    platformType,
  ]);
  // A repeat republish-attempt card's own `id` is a synthetic per-attempt
  // key, not the real channel id - `_channelId` carries the real one so the
  // channel lookup (SFTP flag, avatar, etc.) still resolves correctly.
  const realChannelId = initialdata?._channelId || initialdata?.id;
  const channelFromList = socialChannelList?.find(
    (ch) => ch.id === realChannelId
  );
  const isSftp =
    platformType === "youtube" &&
    !!(
      initialdata?.isSFTPEnabled ||
      initialdata?.is_sftp ||
      channelFromList?.is_sftp
    );

  const dispatch = useDispatch();
  const slateMediaList = useSelector(
    (state: RootState) => state.media.secondaryMediaList.list
  );
  const preMediaList = useSelector(
    (state: RootState) => state.media.preMediaList.list
  );
  const postMediaList = useSelector(
    (state: RootState) => state.media.postMediaList.list
  );
  const mediaChaptersList = useSelector(
    (state: RootState) => state.media.mediaChapters.list
  );
  const mediaChaptersMediaId = useSelector(
    (state: RootState) => state.media.mediaChapters.mediaId
  );
  // Real clips available for this media - any chapter with an id and a
  // start/end range is selectable, regardless of clip_id/enable (those track
  // a separately-generated clip asset, not whether the segment itself is usable).
  // mediaChapters is a single flat (non-keyed) slice shared across every
  // mounted publish card, so it must be gated on mediaId matching the card's
  // own media - otherwise a stale/other media's chapters could briefly leak in.
  const availableClips = useMemo(
    () =>
      mediaChaptersMediaId !== currentMediaDetails?.id
        ? []
        : (mediaChaptersList || []).filter(
            (chapter: ChapterInterface) =>
              !!chapter.id &&
              chapter.start_time !== undefined &&
              chapter.end_time !== undefined
          ),
    [mediaChaptersList, mediaChaptersMediaId, currentMediaDetails?.id]
  );
  // Fetch the media's chapters/clips whenever this card mounts for a media id -
  // several channel cards for the same media may mount at once, so the
  // module-level cache below only dedupes those truly concurrent in-flight
  // requests (cleared once the fetch settles), not future refetches. Without
  // clearing it, clips created later on the Chapters page would never show up
  // here again for the same media within this session.
  useEffect(() => {
    const mediaId = currentMediaDetails?.id;
    if (!mediaId) return;
    if (fetchingCache.chapters.has(mediaId)) return;
    fetchingCache.chapters.add(mediaId);
    dispatch(getListChapters(mediaId) as any)?.finally?.(() => {
      fetchingCache.chapters.delete(mediaId);
    });
  }, [currentMediaDetails?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const socialChannelForCard = useMemo(
    () => socialChannelList?.find((c) => c.id === realChannelId),
    [socialChannelList, realChannelId]
  );

  // True when this specific record has already been published. Cards now
  // render one-per-record (a channel can have several: full/clip/segment),
  // so this must match THIS record's own identity (its own ES id, carried as
  // published_id once synced - see usePublishManagement) rather than
  // channel_name+job_type, which would match any of the channel's other
  // records too. A fresh draft (isNew === 100, no published_id yet) always
  // takes the "not yet published" branch below.
  const isAlreadyPublished = useMemo(
    () =>
      initialdata?.isNew !== 100 &&
      !!initialdata?.published_id &&
      Array.isArray(currentMediaDetails?.socials) &&
      currentMediaDetails.socials.some(
        (s: any) =>
          s.id === initialdata.published_id &&
          s.isNew !== 100 &&
          s.status !== 0 &&
          s.status !== 12
      ),
    [
      currentMediaDetails?.socials,
      initialdata?.isNew,
      initialdata?.published_id,
    ]
  );

  // Slate-specific check: true when initialdata's social record actually
  // exists in currentMediaDetails.socials, regardless of status - unlike
  // isAlreadyPublished above, this doesn't exclude drafts/new (status 0/12).
  // The slate fields (preVideo/postVideo) must reflect that record's real
  // publish history as soon as it exists, not just once it's active/queued.
  const hasExistingSocialRecord = useMemo(
    () =>
      Array.isArray(currentMediaDetails?.socials) &&
      currentMediaDetails.socials.some(
        (s: any) =>
          s.job_type === initialdata?.job_type &&
          s.channel_name === initialdata?.channel_name
      ),
    [
      currentMediaDetails?.socials,
      initialdata?.job_type,
      initialdata?.channel_name,
    ]
  );

  // The "What to publish" mode / clip picker / segment trimmer only ever make
  // sense while creating a *new* publish attempt. Opening a card that's
  // already a real record (isNew !== 100 - draft/queued/completed/failed,
  // not just completed) is metadata-only from here on - even a draft/queue
  // status (0/12) already committed to a mode (e.g. Full video) shouldn't let
  // the user silently switch it to Clips/Segment, which would imply a second,
  // different publish attempt this flow was never meant to trigger. A fresh
  // draft (isNew === 100) is unaffected - it's gated per-mode instead via
  // channelHistory (e.g. hasFullPublish) below.
  const publishRangeLocked =
    (initialdata?.isNew !== 100 && hasExistingSocialRecord) ||
    disabled ||
    isExistingClipRecord;

  // A Segment publish still in draft/queue (status 0/12), or already
  // completed (status 10), can still have its trimmed range adjusted - unlike
  // publishRangeLocked above (which also guards the "What to publish" mode
  // picker against being silently switched), this only reopens the start/end
  // draggers themselves.
  const segmentRangeLocked =
    publishRangeLocked &&
    !(initialdata?.status === 0 || initialdata?.status === 12);

  // Everything already published to this channel for this media - which
  // portion (full/clips/segments), derived straight from the raw socials
  // array (the backend keeps every past record per channel, not just the
  // latest). Only meaningful for gating a *new* republish attempt, so it's
  // computed regardless of card state but only consulted where relevant.
  const channelHistory = useMemo(
    () =>
      buildChannelPublishHistory(currentMediaDetails?.socials, realChannelId),
    [currentMediaDetails?.socials, realChannelId]
  );

  // Time ranges for clips already published to this channel. A clips-type
  // SocialPublish record only stores `publish_clip_id`, not its own
  // start/end (see buildChannelPublishHistory) - so its range has to be
  // resolved by looking the id up against the live `availableClips` list.
  // Folded into the Custom-segment overlap check below so a hand-trimmed
  // range that reuses an already-published clip's span is caught client-side
  // instead of only failing once the RPC round-trips.
  const publishedClipRanges = useMemo(
    () =>
      availableClips
        .filter((clip: ChapterInterface) =>
          channelHistory.publishedClipIds.has(clip.id)
        )
        .map((clip: ChapterInterface) => ({
          start: clip.start_time,
          end: clip.end_time,
        })),
    [availableClips, channelHistory.publishedClipIds]
  );

  // Ranges the Custom-segment overlap check needs beyond what
  // channelHistory.publishedSegments already covers on its own: already-
  // published clip spans (see publishedClipRanges above), plus - since a
  // Full-video publish already covers [0, duration] on this channel, same
  // as if that exact range had been published as a Segment - the whole
  // video's own span once hasFullPublish is true. Passed as `extraRanges`
  // to segmentOverlapsHistory so a "Custom segment" attempt that reuses an
  // already-published clip's range, or just retrims back to (near enough)
  // the whole video, is caught the same way a literal duplicate Segment
  // range already is - instead of the Full-video case only being stopped
  // by the separate hasFullPublish check on the mode picker itself.
  const nonSegmentBlockedRanges = useMemo(() => {
    const ranges = [...publishedClipRanges];
    if (channelHistory.hasFullPublish) {
      ranges.push({ start: 0, end: currentMediaDetails?.meta?.duration || 0 });
    }
    return ranges;
  }, [
    publishedClipRanges,
    channelHistory.hasFullPublish,
    currentMediaDetails?.meta?.duration,
  ]);

  // Same ranges the overlap check above/below already guards against -
  // shading the trimmer against only channelHistory.publishedSegments left
  // an already-published clip's (or the whole video's, via a Full publish)
  // span invisible on the timeline even though trimming onto it was
  // (correctly) still blocked at submit time.
  const blockedSegmentRanges = useMemo(
    () => channelHistory.publishedSegments.concat(nonSegmentBlockedRanges),
    [channelHistory.publishedSegments, nonSegmentBlockedRanges]
  );

  const mediaOrientation = useMemo(() => {
    const orientation = currentMediaDetails?.orientation;
    if (!orientation) return null;
    if (orientation === 2) return "portrait" as const;
    if (orientation === 3) return "square" as const;
    return "landscape" as const;
  }, [currentMediaDetails?.orientation]);

  const [selectedPreSlate, setSelectedPreSlate] = useState<any>(null);
  const [selectedPostSlate, setSelectedPostSlate] = useState<any>(null);
  const [isPreSlateEdit, setIsPreSlateEdit] = useState<boolean>(false);
  const [isPostSlateEdit, setIsPostSlateEdit] = useState<boolean>(false);
  const [preSlateInputText, setPreSlateInputText] = useState<string>("");
  const [postSlateInputText, setPostSlateInputText] = useState<string>("");
  const [userSlateToggle, setUserSlateToggle] = useState<boolean | null>(null);
  const preSlateInputRef = useRef<InputWithMediaInfoRef>(null);
  const postSlateInputRef = useRef<InputWithMediaInfoRef>(null);

  // Reopening an existing (published or draft) destination should show
  // whichever mode it was actually created from, not default back to "full".
  // A *fresh* repeat-attempt card on a channel that already has publish
  // history (no publish_type of its own yet) instead defaults to the next
  // mode still available on that channel - skipping Full once it's been
  // done, and skipping Clips once every available clip is already used.
  // Custom segment trims from the active/live source video, so it's only a
  // valid "What to publish" choice once the media has actually reached that
  // state (state 10 / status 5).
  const isSegmentEligible =
    currentMediaDetails?.state === 10 && currentMediaDetails?.status === 5;
  const [videoRangeMode, setVideoRangeMode] = useState<PublishRangeType>(() => {
    const resolved =
      initialdata?.publish_type ||
      (initialdata?.isNew === 100 && channelHistory.records.length > 0
        ? resolveNextPublishMode(
            channelHistory,
            availableClips.map((c) => c.id)
          )
        : PUBLISH_RANGE_TYPE.FULL);
    return resolved === PUBLISH_RANGE_TYPE.SEGMENT && !isSegmentEligible
      ? PUBLISH_RANGE_TYPE.FULL
      : resolved;
  });
  // True once the user has explicitly picked a "What to publish" mode - stops
  // the auto-resolution effect below from overriding a manual choice once
  // availableClips finishes loading.
  const videoRangeModeTouchedRef = useRef(false);
  const mediaDuration = currentMediaDetails?.meta?.duration || 0;
  // Editing an existing Segment publish (media_start/media_end already set on
  // initialdata) must reopen on that same trimmed range, not reset to the
  // full video - only a fresh draft with no prior range falls back to 0/duration.
  // The backend omits a falsy media_start (0) from the stored ES record
  // instead of persisting it as 0 - a segment genuinely trimmed from the
  // very start of the video comes back with media_start missing entirely,
  // not 0. Gate on media_end alone (a segment always has a real, non-zero
  // end) and default the missing media_start to 0, instead of requiring
  // both fields present - the old check treated that omission as "no saved
  // range at all" and reset the whole range back to [0, full duration],
  // silently discarding the real saved end.
  const hasSavedSegmentRange = initialdata?.media_end != null;
  const [segmentStart, setSegmentStart] = useState(
    hasSavedSegmentRange ? (initialdata?.media_start ?? 0) : 0
  );
  const [segmentEnd, setSegmentEnd] = useState(
    hasSavedSegmentRange ? initialdata.media_end : mediaDuration
  );
  useEffect(() => {
    if (!hasSavedSegmentRange) setSegmentEnd(mediaDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaDuration]);
  const generalPlayerRef = useRef<GeneralPlayerHandle>(null);
  const [segmentPlayerTime, setSegmentPlayerTime] = useState(0);
  const [isSegmentPlayerPaused, setIsSegmentPlayerPaused] = useState(true);
  const [segmentImageReel, setSegmentImageReel] = useState<ImageReel | null>(
    null
  );
  const segmentTrimmerRef = useRef<HTMLDivElement>(null);

  // Clips mode - fully independent of the formik instance below so it can't
  // affect existing sync logic. Directly editing an existing single-clip
  // record seeds its one clip as both selected AND active, with its own
  // field values pulled from initialdata (mirroring toggleClip's normal
  // seeding) - otherwise no clip tab would be "active" and the per-clip
  // fields section would never render, falling back to the dimmed read-only
  // full-video preview instead of this record's own editable fields.
  const [selectedClipIds, setSelectedClipIds] = useState<string[]>(
    isExistingClipRecord ? [initialdata.publish_clip_id] : []
  );
  const [activeClipTabId, setActiveClipTabId] = useState<string | null>(
    isExistingClipRecord ? initialdata.publish_clip_id : null
  );
  const [clipFieldValues, setClipFieldValues] = useState<
    Record<string, ClipFieldValues>
  >(
    isExistingClipRecord
      ? {
          [initialdata.publish_clip_id]: {
            title: initialdata.title || "",
            desc: initialdata.desc || "",
            tags: Array.isArray(initialdata.tags) ? initialdata.tags : [],
            category: initialdata.category || "",
            playlist: initialdata.playlist || "",
            is_created_for_kids: initialdata.is_created_for_kids || "no",
            privacy: initialdata.privacy || config.defaultPrivacy,
            thumb: initialdata.thumb || "",
            thumbId: initialdata.thumb_id || "",
            monetize_policy_id: initialdata.monetize_policy_id || "",
            monetize_policy_name: initialdata.monetize_policy_name || "",
            is_rights_managements_allowed:
              initialdata.is_rights_managements_allowed || "yes",
          },
        }
      : {}
  );
  // Only show a clip field's required-field error after a Publish attempt -
  // not while the user is still filling the form in for the first time.
  const [clipFieldsTouched, setClipFieldsTouched] = useState(false);
  // True while the selected clips are being published one-by-one (sequential
  // socialpublish.create calls) - gates the Publish button so a second click
  // can't overlap the in-flight sequence.
  const [isPublishingClips, setIsPublishingClips] = useState(false);
  // Gates the Delete button on a "Resolving..." state until the
  // takedown/delete RPC (awaited in handleCancelOrDelete) actually resolves
  // - the sheet must not close on click alone, only on confirmed success.
  const [isDeletingPublish, setIsDeletingPublish] = useState(false);

  const [monetizationOptions, setMonetizationOptions] = useState<
    MonetizationOptions[]
  >([]);
  const [showScheduler, setShowScheduler] = useState(
    initialdata?.schedule ? true : false
  );
  const [scheduled, setScheduled] = useState<number | null>(
    initialdata?.schedule ? new Date(initialdata?.schedule).getTime() : null
  );
  const [isValidImage, setIsValidImage] = useState(true);
  const [durationError, setDurationError] = useState<string | null>(null);
  const [socialData, setSocialData] = useState<any>(null);
  // Seeded from the publish object so the choice survives the card unmount/remount.
  // Replaces the old "Social Publishing On/Off" toggle: it is now set implicitly by
  // which footer button the user clicks (Save → false/draft, Publish → true/queue)
  // and persisted so the parent guard (isChipActive/_markActive) keeps working.
  const [markActive, setMarkActive] = useState<boolean>(
    typeof initialdata?._markActive === "boolean"
      ? initialdata._markActive
      : true
  );
  // Which footer button drove the current submit: "save" stages as draft (no
  // validation), "publish" queues (validated in handlePublish before submitting).
  const submitModeRef = useRef<"save" | "publish">("save");

  // Resolve slate ID from a channel-like source by the media's orientation,
  // falling back to the legacy flat pre/post string fields.
  const resolveSlateByOrientation = (
    source:
      | Pick<
          SocialChannel,
          "pre" | "post" | "landscape_slate" | "portrait_slate" | "square_slate"
        >
      | null
      | undefined,
    key: "pre" | "post"
  ): string => {
    if (!source || !mediaOrientation) return "";
    const orientationSlate = source[`${mediaOrientation}_slate`] as
      | PrePost
      | undefined;
    if (orientationSlate?.[key]) return orientationSlate[key];
    const flat = source[key];
    if (typeof flat === "string") return flat;
    return "";
  };

  // Prefer orientation slates from the preset's channel config (socialData)
  // when available; fall back to the social channel's own config.
  // socialData is null on first render — the useEffect below overwrites
  // preVideo/postVideo once socialData arrives.
  // Already-published media has a real publish history (its actual preVideo/
  // postVideo, seeded below) — the channel's *current* default slate config
  // must not override/re-apply onto it, or a media published with no slate at
  // all would start showing the channel's default slate as if it had one.
  const channelPreSlate = hasExistingSocialRecord
    ? ""
    : socialData
      ? resolveSlateByOrientation(socialData, "pre")
      : resolveSlateByOrientation(socialChannelForCard, "pre");
  const channelPostSlate = hasExistingSocialRecord
    ? ""
    : socialData
      ? resolveSlateByOrientation(socialData, "post")
      : resolveSlateByOrientation(socialChannelForCard, "post");

  const slateBandExpanded =
    userSlateToggle ?? (!!channelPreSlate || !!channelPostSlate);
  const setSlateBandExpanded = (next: boolean | ((prev: boolean) => boolean)) =>
    setUserSlateToggle((prev) => {
      const current = prev ?? (!!channelPreSlate || !!channelPostSlate);
      return typeof next === "function" ? next(current) : next;
    });
  const shouldValidateTagsRef = useRef(false);
  // Which synced fields the user has manually edited on this card, and whether the
  // card has been saved. These are SEEDED from initialdata (the publish object) so
  // they survive the card unmounting when the sheet closes and remounting on
  // reopen — a plain useRef would reset to all-false on every remount and the sync
  // would clobber the user's manual edits. markFieldEdited/onCardSaved push the
  // change back up to the parent so initialdata stays authoritative.
  const editedFieldsRef = useRef<{
    title: boolean;
    desc: boolean;
    tags: boolean;
    thumbnail: boolean;
  }>({
    title: !!initialdata?._editedFields?.title,
    desc: !!initialdata?._editedFields?.desc,
    tags: !!initialdata?._editedFields?.tags,
    thumbnail: !!initialdata?._editedFields?.thumbnail,
  });
  const isCardSavedRef = useRef(!!initialdata?._cardSaved);
  // Merge (union, never clear) any edited flags that arrive on initialdata after
  // mount — e.g. the backend's `edited` array landing via a late media refetch, or
  // the parent re-seeding on wizard Back. This guarantees a subsequent edit sends
  // the previously-edited fields together with the newly-edited one.
  useEffect(() => {
    const incoming = initialdata?._editedFields;
    if (!incoming) return;
    (["title", "desc", "tags", "thumbnail"] as const).forEach((f) => {
      if (incoming[f]) editedFieldsRef.current[f] = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialdata?._editedFields?.title,
    initialdata?._editedFields?.desc,
    initialdata?._editedFields?.tags,
    initialdata?._editedFields?.thumbnail,
  ]);
  const markFieldEdited = useCallback(
    (field: "title" | "desc" | "tags" | "thumbnail") => {
      editedFieldsRef.current[field] = true;
      onFieldEdited?.(initialdata?.id, field);
    },
    [onFieldEdited, initialdata?.id]
  );
  const [enabledFields, setEnabledFields] = useState<Record<string, boolean>>({
    title: true,
    desc: true,
    tags: true,
    privacy: true,
    schedule_after: true,
    title_ai_checked: false,
    desc_ai_checked: false,
    title_ai_type: false,
    desc_ai_type: false,
    title_ai_value: false,
    desc_ai_value: false,
    // YouTube-specific fields - initialize based on platform
    category: platformType === "youtube" ? false : false,
    monetize_policy_id: platformType === "youtube" ? false : false,
    playlist: platformType === "youtube" ? false : false,
    is_created_for_kids: platformType === "youtube" ? false : false,
    is_rights_managements_allowed: platformType === "youtube" ? false : false,
    conditional_rules: true,
    tags_mode: false,
  });

  function extractIdFromUrl(url: string) {
    if (!url) return null;
    const parts = url.split("/").pop();
    const id = parts?.split("_")[0];
    return id?.split(".")[0];
  }

  // Memoize thumbId extraction to avoid recalculation on every render
  const thumbId = useMemo(() => {
    const extracted = initialdata?.thumb_id || "";
    return extracted !== "undefined" ? extracted : "";
  }, [initialdata?.thumb_id]);

  // Get social data from item or presetDetails
  useEffect(() => {
    if (item?.social?.length > 0) {
      const socialItem = item.social.find(
        (social: any) =>
          social.job_type === initialdata?.job_type &&
          social.channel_name === initialdata?.channel_name
      );
      if (socialItem) {
        setSocialData(socialItem);
      }
    }
    if (
      item?.social?.length === 0 ||
      item?.social === undefined ||
      item === null
    ) {
      const socialItem = presetDetails?.find(
        (social: any) =>
          social.job_type === initialdata?.job_type &&
          social.channel_name === initialdata?.channel_name
      );
      if (socialItem) {
        setSocialData(socialItem);
      }
    }
  }, [item, presetDetails]);

  const handlePreviewAiTranslate = async (
    field: string,
    text?: string,
    textToTranslate?: string,
    prefix?: string,
    suffix?: string
  ) => {
    if (field === "title") {
      setIsTitleBlurLoading(true);
    } else {
      setIsDescriptionBlurLoading(true);
    }
    try {
      const response = await store.dispatch(
        aiService({
          title: textToTranslate,
          id: initialdata?.media_id,
          service: "prompt",
          target_language: text,
        })
      );

      if (response) {
        const safePrefix = prefix ?? "";
        const safeSuffix = suffix ?? "";
        const safeOutput = response.output_processed?.output ?? "";
        const finalText = [safePrefix, safeOutput, safeSuffix]
          .filter(Boolean)
          .join(" ");
        if (field === "title") {
          markFieldEdited("title");
          formik.setFieldValue("title", finalText);
        } else {
          markFieldEdited("desc");
          formik.setFieldValue("desc", finalText);
        }
      }
    } catch (error) {
      console.error("Error in AI service:", error);
    } finally {
      if (field === "title") {
        setIsTitleBlurLoading(false);
      } else {
        setIsDescriptionBlurLoading(false);
      }
    }
  };

  // Update form values when socialData changes
  useEffect(() => {
    if (socialData && formik.values) {
      const hasTitleAi =
        socialData?.title_ai_value && socialData.title_ai_value.trim() !== "";
      const hasDescAi =
        socialData?.desc_ai_value && socialData.desc_ai_value.trim() !== "";

      formik.setFieldValue("title_ai_checked", hasTitleAi);
      formik.setFieldValue("desc_ai_checked", hasDescAi);
      formik.setFieldValue("title_ai_type", socialData?.title_ai_type || "");
      formik.setFieldValue("desc_ai_type", socialData?.desc_ai_type || "");
      formik.setFieldValue("title_ai_value", socialData?.title_ai_value || "");
      formik.setFieldValue("desc_ai_value", socialData?.desc_ai_value || "");

      const aiFields = [
        "title_ai_type",
        "title_ai_value",
        "desc_ai_type",
        "desc_ai_value",
        "title_ai_checked",
        "desc_ai_checked",
      ];

      // Explicitly handle YouTube-specific fields
      if (platformType === "youtube") {
        if (
          socialData?.category !== undefined &&
          socialData?.category !== null &&
          socialData?.category !== ""
        ) {
          formik.setFieldValue("category", socialData.category);
        }
        if (
          socialData?.monetize_policy_id !== undefined &&
          socialData?.monetize_policy_id !== null &&
          socialData?.monetize_policy_id !== ""
        ) {
          formik.setFieldValue(
            "monetize_policy_id",
            socialData.monetize_policy_id
          );
        }
        if (
          socialData?.monetize_policy_name !== undefined &&
          socialData?.monetize_policy_name !== null &&
          socialData?.monetize_policy_name !== ""
        ) {
          formik.setFieldValue(
            "monetize_policy_name",
            socialData.monetize_policy_name
          );
        }
        if (
          socialData?.playlist !== undefined &&
          socialData?.playlist !== null &&
          socialData?.playlist !== ""
        ) {
          formik.setFieldValue("playlist", socialData.playlist);
        }
        if (
          socialData?.is_created_for_kids !== undefined &&
          socialData?.is_created_for_kids !== null &&
          socialData?.is_created_for_kids !== ""
        ) {
          formik.setFieldValue(
            "is_created_for_kids",
            socialData.is_created_for_kids
          );
        }
        if (
          socialData?.is_rights_managements_allowed !== undefined &&
          socialData?.is_rights_managements_allowed !== null &&
          socialData?.is_rights_managements_allowed !== ""
        ) {
          formik.setFieldValue(
            "is_rights_managements_allowed",
            socialData.is_rights_managements_allowed
          );
        }
      }

      Object.keys(socialData).forEach((key) => {
        if (
          socialData[key] !== undefined &&
          socialData[key] !== null &&
          socialData[key] !== "" &&
          !aiFields.includes(key) &&
          key !== "tags"
        ) {
          if (key in formik.values) {
            formik.setFieldValue(key, socialData[key]);
          }
        }
      });

      // Handle tags based on tags_mode
      const tagsMode = socialData?.tags_mode;
      const socialTags: string[] = socialData?.tags ?? [];
      const currentTags: string[] = formik.values.tags ?? [];

      formik.setFieldValue(
        "tags",
        resolveTagsByMode({
          mode: tagsMode,
          currentTags,
          incomingTags: socialTags,
          // Keep existing behavior: if mode is missing/unknown, treat as OVERRIDE
          defaultMode: PRESET_MODES.OVERRIDE,
        })
      );

      if (socialData?.schedule_after && socialData?.schedule_after > 0) {
        setShowScheduler(true);
        const scheduledTime =
          new Date().getTime() + socialData?.schedule_after * 60 * 1000;
        formik.setFieldValue("privacy", "schedule");
        setScheduled(scheduledTime);
      }

      // Resolve slates from the preset's channel config by orientation.
      // socialData carries landscape_slate/portrait_slate/square_slate from
      // ChannelConfigs (spread via ...rest in CommonFunctions). This runs
      // whenever socialData is set (preset selected on publish page or in
      // preset editor) and must be here because socialData is null at
      // formik initialValues time.
      if (mediaOrientation && !hasExistingSocialRecord) {
        const orientationSlate = socialData?.[`${mediaOrientation}_slate`] as
          | PrePost
          | undefined;
        // Only override if the preset has orientation slate data for this channel
        if (orientationSlate !== undefined) {
          formik.setFieldValue("preVideo", orientationSlate.pre ?? "");
          formik.setFieldValue("postVideo", orientationSlate.post ?? "");
        }
      }

      const enableFieldValues: Record<string, boolean> = {
        title:
          platformType === "youtube" ? true : socialData?.title ? true : false,
        conditional_rules: true,
        desc:
          platformType === "youtube" ? true : socialData?.desc ? true : false,
        tags:
          platformType === "youtube" ? true : socialData?.tags ? true : false,
        privacy:
          platformType === "youtube"
            ? true
            : socialData?.privacy
              ? true
              : false,
        title_ai_checked: socialData?.title_ai_type ? true : false,
        desc_ai_checked: socialData?.desc_ai_type ? true : false,
        title_ai_type: socialData?.title_ai_type ? true : false,
        desc_ai_type: socialData?.desc_ai_type ? true : false,
        title_ai_value: socialData?.title_ai_value ? true : false,
        desc_ai_value: socialData?.desc_ai_value ? true : false,
        schedule_after: true,
        // YouTube-specific fields
        category:
          platformType === "youtube"
            ? socialData?.category
              ? true
              : false
            : false,
        monetize_policy_id:
          platformType === "youtube"
            ? socialData?.monetize_policy_id
              ? true
              : false
            : false,
        playlist:
          platformType === "youtube"
            ? socialData?.playlist
              ? true
              : false
            : false,
        is_created_for_kids:
          platformType === "youtube"
            ? socialData?.is_created_for_kids
              ? true
              : false
            : false,
        is_rights_managements_allowed:
          platformType === "youtube"
            ? socialData?.is_rights_managements_allowed
              ? true
              : false
            : false,
      };

      if (
        !isPreview &&
        !isPreset &&
        !isAlreadyPublished &&
        !initialdata?._affixApplied
      ) {
        const normalizPreTitle = socialData?.title_prepend || "";
        const normalizPostTitle = socialData?.title_append || "";
        const normalizPreDesc = socialData?.description_prepend || "";
        const normalizPostDesc = socialData?.description_append || "";
        if (!editedFieldsRef.current.title) {
          if (
            socialData?.title_ai_type?.length > 0 &&
            socialData?.title_ai_type !== "None"
          ) {
            handlePreviewAiTranslate(
              "title",
              socialData?.title_ai_value,
              initialdata?.title,
              normalizPreTitle,
              normalizPostTitle
            );
          } else {
            formik.setFieldValue(
              "title",
              composeAffixed(
                initialdata?.title || "",
                normalizPreTitle,
                normalizPostTitle
              )
            );
          }
        }
        if (!editedFieldsRef.current.desc) {
          if (
            socialData?.desc_ai_type?.length > 0 &&
            socialData?.desc_ai_type !== "None"
          ) {
            handlePreviewAiTranslate(
              "description",
              socialData?.desc_ai_value,
              initialdata?.desc,
              normalizPreDesc,
              normalizPostDesc
            );
          } else {
            formik.setFieldValue(
              "desc",
              composeAffixed(
                initialdata?.desc || "",
                normalizPreDesc,
                normalizPostDesc
              )
            );
          }
        }
      }
      setEnabledFields(enableFieldValues);
    }
  }, [socialData]);

  const getEnabledFieldsPayload = useCallback(
    (values: any) => {
      const payload: any = {
        job_type: platformType,
        upload_type: platformType === "youtube" ? "sftp" : "api",
        media: "",
        platform: values.id,
        channel_name: values.channel_name,
      };

      // YouTube-specific fields that should always be included if they have values
      const youtubeSpecificFields = [
        "category",
        "monetize_policy_id",
        "playlist",
        "is_created_for_kids",
        "is_rights_managements_allowed",
      ];

      Object.keys(enabledFields).forEach((fieldName) => {
        // For YouTube platform, always include YouTube-specific fields if they have values
        const isYoutubeField =
          platformType === "youtube" &&
          youtubeSpecificFields.includes(fieldName);
        const shouldInclude = isYoutubeField
          ? values[fieldName] !== undefined &&
            values[fieldName] !== null &&
            values[fieldName] !== ""
          : enabledFields[fieldName] &&
            values[fieldName] !== undefined &&
            values[fieldName] !== "";

        if (shouldInclude) {
          if (Array.isArray(values[fieldName])) {
            if (values[fieldName].length > 0) {
              payload[fieldName] = values[fieldName];
            }
          } else {
            // For playlist, check if it's a non-empty string
            if (
              fieldName === "playlist" &&
              typeof values[fieldName] === "string"
            ) {
              const playlistArray = values[fieldName]
                .split(",")
                .filter(Boolean);
              if (playlistArray.length > 0) {
                payload[fieldName] = values[fieldName];
              }
            } else {
              payload[fieldName] = values[fieldName];
            }
          }
        }
      });

      return payload;
    },
    [enabledFields, platformType]
  );

  const formik = useFormik<BaseFormValues>({
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      // A repeat republish-attempt card's own `id` is a synthetic per-card
      // key (see `_channelId` on SocialPublish) used only to avoid local
      // React-state/id collisions with the still-rendered original card -
      // it must never be sent to the backend as the destination identity.
      // realChannelId resolves to the real channel connection id in both
      // cases (first-time publish: initialdata.id itself; repeat attempt:
      // _channelId).
      id: realChannelId,
      platform: realChannelId,
      channel_name: initialdata.channel_name || "",
      // An in-progress edit on an already-persisted destination is kept off
      // the collapsed card's own fields (so it doesn't show unconfirmed
      // content) and instead lives in _editedValues, restored here so
      // switching tabs in the sheet and coming back doesn't lose it. Fully
      // closing the sheet without saving clears _editedValues (see
      // removeSheetDestinations), so a genuine discard doesn't linger.
      title: isPreset
        ? ""
        : (initialdata?._editedValues?.title ?? initialdata?.title) || "",
      title_ai_type: socialData?.title_ai_type || "",
      title_ai_value: socialData?.title_ai_value || "",
      title_ai_checked: socialData?.title_ai_type ? true : false,
      desc: isPreset
        ? ""
        : (initialdata?._editedValues?.desc ?? initialdata?.desc) || "",
      desc_ai_type: socialData?.desc_ai_type || "",
      desc_ai_value: socialData?.desc_ai_value || "",
      desc_ai_checked: socialData?.desc_ai_type ? true : false,
      tags: isPreset
        ? []
        : Array.isArray(initialdata?._editedValues?.tags)
          ? initialdata._editedValues.tags
          : Array.isArray(initialdata?.tags)
            ? initialdata?.tags
            : [],
      thumb: initialdata?._editedValues?.thumb
        ? initialdata._editedValues.thumb
        : initialdata?.thumb_id
          ? `${mediaBaseUrl}/${initialdata?.thumb_id}${
              currentMediaDetails?.source === "dbsync" ? ".jpg" : ".jpg"
            }`
          : getThumbnailUrlUtil({
              media: currentMediaDetails,
              id: resolveThumbIdByOrientation(currentMediaDetails) || "",
              source: currentMediaDetails?.source,
            }),
      thumbId: initialdata?._editedValues?.thumb_id
        ? initialdata._editedValues.thumb_id
        : initialdata?.thumb_id
          ? initialdata?.thumb_id
          : resolveThumbIdByOrientation(currentMediaDetails) || "",
      status: initialdata?.status,
      msg: initialdata?.msg || "",
      privacy: isPreset
        ? config.defaultPrivacy
        : initialdata?.schedule && initialdata?.schedule >= Date.now()
          ? "schedule"
          : initialdata?.privacy || config.defaultPrivacy,
      job_type: initialdata?.job_type,
      upload_type: initialdata?.upload_type,
      media_id: initialdata?.media_id,
      ct: initialdata?.ct || Date.now(),
      mt: initialdata?.mt || Date.now(),
      schedule: initialdata?.schedule || undefined,
      isNew: initialdata?.isNew,
      schedule_after: socialData?.schedule_after || 0,
      // Platform-specific defaults
      category: isPreset
        ? socialData?.category || ""
        : initialdata?.category || "",
      monetize_policy_id: isPreset
        ? socialData?.monetize_policy_id || ""
        : initialdata?.monetize_policy_id || "",
      monetize_policy_name: isPreset
        ? socialData?.monetize_policy_name || ""
        : initialdata?.monetize_policy_name || "",
      playlist: isPreset
        ? socialData?.playlist || ""
        : initialdata?.playlist || "",
      is_created_for_kids: isPreset
        ? socialData?.is_created_for_kids || "no"
        : initialdata?.is_created_for_kids || "no",
      is_rights_managements_allowed: isPreset
        ? socialData?.is_rights_managements_allowed || "yes"
        : initialdata?.is_rights_managements_allowed || "yes",
      pageName: initialdata?.pageName || "",
      image: initialdata?.image || "",
      isChecked: socialData?.isChecked || false,
      conditional_rules: isPreset
        ? socialData?.conditional_rules || []
        : initialdata?.conditional_rules || [],
      preVideo: hasExistingSocialRecord
        ? (initialdata?.preVideo ?? "")
        : (initialdata?.preVideo ?? channelPreSlate ?? ""),
      postVideo: hasExistingSocialRecord
        ? (initialdata?.postVideo ?? "")
        : (initialdata?.postVideo ?? channelPostSlate ?? ""),
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .trim()
        .required("Title is required")
        .test("title-length", "Title exceeds platform limit", (value) =>
          config.validateTitle(value || "")
        ),
      desc: config.showDescription
        ? Yup.string()
            .trim()
            .required("Description is required")
            .min(5, "Description should be at least 5 characters")
            .test(
              "desc-length",
              "Description exceeds platform limit",
              (value) =>
                config.validateDescription
                  ? config.validateDescription(value || "")
                  : true
            )
        : Yup.string(),
      tags: Yup.array()
        .of(Yup.string().trim().required("Tags cannot be empty"))
        .test(
          "tags-required",
          "At least one tag is required",
          function (value) {
            if (!shouldValidateTagsRef.current) {
              return true;
            }
            return value && value.length > 0;
          }
        )
        .test(
          "tags-total-char-limit",
          "Tags must be less than 500 characters",
          function (value) {
            if (!value || !Array.isArray(value)) return true;
            const { tagCount } = calculateTagCount(
              value,
              platformType === "youtube"
            );
            return tagCount <= 500;
          }
        )
        .test(
          "youtube-tag-byte-limit",
          YOUTUBE_SOCIAL_TAG_LENGTH_ERROR_MESSAGE,
          function (value) {
            if (platformType !== "youtube") return true;
            return areYoutubeSocialTagsCharLengthValid(value);
          }
        ),

      ...(isPreset
        ? { thumb: Yup.string() }
        : { thumb: Yup.string().required("Thumbnail is required") }),
      ...(!isPreset && {
        thumbId: Yup.string().required("Thumbnail is required"),
      }),
      ...(platformType === "youtube" && {
        category: Yup.string().required("Category is required"),
        ...(isSftp && {
          monetize_policy_id: Yup.string().required("Monetization is required"),
          is_rights_managements_allowed: Yup.string().required(
            "Rights managements is required"
          ),
        }),
        is_created_for_kids: Yup.string().required(
          "Created for kids is required"
        ),
      }),
    }),
    onSubmit: (values) => {
      submitValuesRef.current(values);
    },
  });

  // Whether the form changed since sync unlocked it — gates the "Update" button.
  // Clips mode edits via clipFieldValues (see the per-clip tab editor below),
  // not formik.values, so that map must be compared too - otherwise editing
  // a clip's title/desc/tags after Sync never registers as a change.
  const hasChangesSincePublishSync =
    publishEditStage === "update" &&
    publishSyncSnapshotRef.current !== null &&
    (JSON.stringify(formik.values) !== publishSyncSnapshotRef.current ||
      (isExistingClipRecord &&
        clipFieldValuesSyncSnapshotRef.current !== null &&
        JSON.stringify(clipFieldValues) !==
          clipFieldValuesSyncSnapshotRef.current));

  // Visibility going from "public" (as of the last sync) to "private"/"unlisted"
  // hides already-published content on the platform — warn before letting it through.
  const syncedPrivacy = (() => {
    if (!publishSyncSnapshotRef.current) return undefined;
    try {
      return JSON.parse(publishSyncSnapshotRef.current)?.privacy;
    } catch {
      return undefined;
    }
  })();
  const isVisibilityDowngradedSinceSync =
    hasChangesSincePublishSync &&
    syncedPrivacy === "public" &&
    (formik.values.privacy === "private" ||
      formik.values.privacy === "unlisted");
  const selectedVisibilityLabel =
    formik.values.privacy === "unlisted" ? "Unlisted" : "Private";

  const submitValues = (values: BaseFormValues) => {
    isCardSavedRef.current = true;
    // Saving freezes the card: every field is now "edited" so nothing ever
    // re-syncs from the media again, and the backend gets the full edited set.
    editedFieldsRef.current = {
      title: true,
      desc: true,
      tags: true,
      thumbnail: true,
    };
    (["title", "desc", "tags", "thumbnail"] as const).forEach((f) =>
      onFieldEdited?.(initialdata?.id, f)
    );
    // onCardSaved clears this destination's "unsaved changes" flag - it must
    // run AFTER the onFieldEdited calls above, which (as a side effect of
    // freezing the edited-fields bookkeeping) mark it unsaved again. Calling
    // it first left every successfully saved/published/updated card
    // permanently flagged as unsaved, so reopening it later and clicking
    // outside without touching anything still showed the discard-changes
    // confirm.
    onCardSaved?.(initialdata?.id);
    values.title = values.title?.trim() ?? values.title;
    values.desc = values.desc?.trim() ?? values.desc;
    // Clips mode edits the active clip's title/desc/tags/etc. via
    // clipFieldValues, not formik.values - merge it in before building the
    // payload used for Save/Update on an existing record (the separate
    // multi-clip fanout further below, for a brand-new create, does its own
    // per-clip merge and isn't affected by this). Without this, saving/
    // updating an existing Clips-type destination sent formik's stale
    // values and silently dropped whatever was typed in the clip editor.
    const activeClip =
      videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS && activeClipTabId
        ? clipFieldValues[activeClipTabId]
        : undefined;
    if (activeClip) {
      values = {
        ...values,
        title: activeClip.title?.trim() ?? activeClip.title ?? values.title,
        desc: activeClip.desc?.trim() ?? activeClip.desc ?? values.desc,
        // Array.isArray, not `.length`, so an intentionally-cleared (empty)
        // tags list is respected instead of falling back to the stale
        // formik tags whenever the clip's own tags are empty.
        tags: Array.isArray(activeClip.tags) ? activeClip.tags : values.tags,
        category: activeClip.category || values.category,
        playlist: activeClip.playlist ?? values.playlist,
        is_created_for_kids:
          activeClip.is_created_for_kids ?? values.is_created_for_kids,
        privacy: activeClip.privacy || values.privacy,
        thumb: activeClip.thumb || values.thumb,
        thumbId: activeClip.thumbId || values.thumbId,
        monetize_policy_id:
          activeClip.monetize_policy_id || values.monetize_policy_id,
        monetize_policy_name:
          activeClip.monetize_policy_name || values.monetize_policy_name,
        is_rights_managements_allowed:
          activeClip.is_rights_managements_allowed ??
          values.is_rights_managements_allowed,
      };
    }
    const publishPayload = config.buildPayload(
      values,
      initialdata,
      enabledFields
    );
    publishPayload.edited = editedFieldsToArray(editedFieldsRef.current);
    // TEMP DEBUG
    console.log("[CAT_SUBMIT]", {
      formikCategory: values.category,
      payloadCategory: publishPayload.category,
      enabledCategory: enabledFields.category,
    });

    // The footer toggle is gone; the clicked button decides the outcome:
    //   Save    → stage as DRAFT (12), no validation (handleSave)
    //   Publish → QUEUE (0); handlePublish already gated on validation
    const isPublishAction = submitModeRef.current === "publish";
    const isRetryAction =
      isPublishAction &&
      (initialdata?.status === SOCIAL_PUBLISH_STATUS.FAILED ||
        initialdata?.status === -2);
    if (isRetryAction) {
      // Retry on a failed destination must re-queue it, even though it's
      // already "published" (has a real record) and would otherwise be
      // excluded from the status write below.
      publishPayload.status = SOCIAL_PUBLISH_STATUS.QUEUE;
    } else if (initialdata?.isNew === 100 || !isAlreadyPublished) {
      publishPayload.status = isPublishAction
        ? SOCIAL_PUBLISH_STATUS.QUEUE
        : SOCIAL_PUBLISH_STATUS.DRAFT;
    }

    if (mediaOrientation === "portrait" && platformType !== "dailymotion") {
      delete publishPayload.thumb;
      delete publishPayload.thumb_id;
    }

    const hasOrientation =
      !!currentMediaDetails?.orientation &&
      currentMediaDetails.orientation !== 0;
    const resolvedPre =
      hasOrientation && values.preVideo && values.preVideo.length > 0
        ? values.preVideo
        : "";
    const resolvedPost =
      hasOrientation && values.postVideo && values.postVideo.length > 0
        ? values.postVideo
        : "";
    if (resolvedPre) {
      publishPayload.preVideo = resolvedPre;
    }
    if (resolvedPost) {
      publishPayload.postVideo = resolvedPost;
    }
    // publishPayload.pre = resolvedPre;
    // publishPayload.post = resolvedPost;

    if (isPreset) {
      const payload = getEnabledFieldsPayload(values);
      payload.title_ai_type = values.title_ai_type;
      payload.title_ai_value = values.title_ai_value;
      payload.desc_ai_type = values.desc_ai_type;
      payload.desc_ai_value = values.desc_ai_value;
      // Keep schedule as schedule in preset payload - buildPayload will handle conversion if needed
      if (handleAllSelectedPresets) {
        handleAllSelectedPresets(payload as any);
      }
    } else {
      // A Segment publish still in draft/queue (status 0/12), or already
      // completed (status 10), has its range unlocked for editing (see
      // segmentRangeLocked) - carry whatever the trimmer currently shows into
      // the update payload, same as at creation time, so a changed
      // media_start/media_end actually persists on Save/Publish.
      if (
        initialdata?.isNew !== 100 &&
        videoRangeMode === PUBLISH_RANGE_TYPE.SEGMENT &&
        (initialdata?.status === 0 ||
          initialdata?.status === 12 ||
          initialdata?.status === 10)
      ) {
        publishPayload.media_start = segmentStart;
        publishPayload.media_end = segmentEnd;
      }
      if (initialdata?.isNew === 100) {
        // Guard against duplicating history already published to this
        // channel - the UI already disables these choices (full-video card,
        // published clips, overlapping segment ranges), but a fresh attempt
        // could still reach here via a stale render, so re-check here too.
        if (
          videoRangeMode === PUBLISH_RANGE_TYPE.FULL &&
          channelHistory.hasFullPublish
        ) {
          toast.fail(
            "This channel already has a full-video publish for this media. Choose Clips or Custom segment instead."
          );
          return;
        }
        if (
          videoRangeMode === PUBLISH_RANGE_TYPE.SEGMENT &&
          segmentOverlapsHistory(
            channelHistory,
            segmentStart,
            segmentEnd,
            SEGMENT_BUFFER_SECONDS,
            nonSegmentBlockedRanges
          )
        ) {
          toast.fail(
            `This range is nearly identical to a segment already published to this channel (start/end within ${SEGMENT_BUFFER_SECONDS}s). Choose a different range.`
          );
          return;
        }

        // Record what portion of the video this destination was created
        // from (Full video / Clips / Custom segment) - only meaningful at
        // creation time, not on later edits/updates of an existing publish.
        publishPayload.publish_type = videoRangeMode;
        if (videoRangeMode === PUBLISH_RANGE_TYPE.SEGMENT) {
          // The trimmed range only makes sense at creation time - a later
          // Edit -> Sync -> Update of an already-published destination goes
          // through the isVideoUpdateSubmitRef branch below, not here.
          publishPayload.media_start = segmentStart;
          publishPayload.media_end = segmentEnd;
        } else if (
          videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS &&
          activeClipTabId
        ) {
          publishPayload.publish_clip_id = activeClipTabId;
        }
        if (onStageDraft) {
          onStageDraft(initialdata.id, publishPayload);
        } else if ((initialdata as any)?.autoAdded && onPublished) {
          formik.setFieldValue("isNew", 0);
          formik.setFieldValue("status", 10);
          formik.setFieldValue("msg", "Published");
          formik.setFieldValue("ct", Date.now());
          onPublished(initialdata.id, { auto: true });
        } else if (
          videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS &&
          selectedClipIds.length > 0
        ) {
          // One social publish "create" call per selected clip, run
          // sequentially (each call resolves before the next starts) rather
          // than in parallel - matches the single-destination flow above but
          // fanned out across every chosen clip. Already-published clips are
          // disabled in the picker, but re-filter here defensively in case
          // channelHistory updated after the clips were selected.
          setIsPublishingClips(true);
          const clipIdsToPublish = selectedClipIds.filter(
            (clipId) => !channelHistory.publishedClipIds.has(clipId)
          );
          const publishClipsSequentially = async () => {
            let lastResp: any = null;
            for (const clipId of clipIdsToPublish) {
              const clip = availableClips.find((c) => c.id === clipId);
              const clipValues = clipFieldValues[clipId];
              // Thumbnail priority: an explicit per-clip override the user
              // picked in the clip's own thumbnail field, then the clip's own
              // auto-generated frame (base + thumb id, as a .jpg), then the
              // parent video's thumbnail as a last resort.
              const clipAutoThumb =
                clip?.base && clip?.thumb
                  ? `${clip.base}/${clip.thumb}.jpg`
                  : "";
              const mergedValues = {
                ...values,
                title: clipValues?.title || values.title,
                desc: clipValues?.desc || values.desc,
                // Array.isArray, not `.length`, so an intentionally-cleared
                // (empty) tags list is respected instead of falling back to
                // the stale formik tags.
                tags: Array.isArray(clipValues?.tags)
                  ? clipValues.tags
                  : values.tags,
                category: clipValues?.category || values.category,
                playlist: clipValues?.playlist ?? values.playlist,
                is_created_for_kids:
                  clipValues?.is_created_for_kids ?? values.is_created_for_kids,
                privacy: clipValues?.privacy || values.privacy,
                thumb: clipValues?.thumb || clipAutoThumb || values.thumb,
                thumbId: clipValues?.thumbId || clip?.thumb || values.thumbId,
                monetize_policy_id:
                  clipValues?.monetize_policy_id || values.monetize_policy_id,
                monetize_policy_name:
                  clipValues?.monetize_policy_name ||
                  values.monetize_policy_name,
                is_rights_managements_allowed:
                  clipValues?.is_rights_managements_allowed ??
                  values.is_rights_managements_allowed,
              };
              const clipPayload = config.buildPayload(
                mergedValues,
                initialdata,
                enabledFields
              );
              clipPayload.edited = publishPayload.edited;
              clipPayload.status = publishPayload.status;
              if (resolvedPre) clipPayload.preVideo = resolvedPre;
              if (resolvedPost) clipPayload.postVideo = resolvedPost;
              clipPayload.publish_type = PUBLISH_RANGE_TYPE.CLIPS;
              clipPayload.publish_clip_id = clipId;
              if (clip) {
                clipPayload.media_start = clip.start_time;
                clipPayload.media_end = clip.end_time;
              }
              lastResp = await new Promise((resolve) => {
                callPublishMedia(clipPayload, (resp: any) => resolve(resp));
              });
            }
            return lastResp;
          };
          // The card's own isNew/status/msg are never set from this response
          // - the card only ever reflects currentMediaDetails.socials, which
          // updates once the social.update socket message arrives for the
          // newly created record(s). onPublished still fires so the parent
          // can clear the Save & Publish guard / surface a failure toast.
          // Closing the sheet (via onPublished) must happen once the LAST
          // clip's sequential call resolves, not per-clip - and it must fire
          // even if one of the calls throws instead of resolving through its
          // callback, or the sheet would never close for a multi-clip
          // Save/Publish.
          publishClipsSequentially()
            .then((resp: any) => {
              onPublished?.(initialdata.id, {
                status: resp?.status,
                msg: resp?.msg,
                saved: !isPublishAction,
              });
            })
            .catch(() => {
              onPublished?.(initialdata.id, { status: -1 });
            })
            .finally(() => setIsPublishingClips(false));
        } else {
          // Same as above: don't mutate the card's own state from `resp` -
          // it only ever reflects currentMediaDetails.socials.
          callPublishMedia(publishPayload, (resp: any) => {
            onPublished?.(initialdata.id, {
              status: resp?.status,
              msg: resp?.msg,
              saved: !isPublishAction,
            });
          });
        }
      } else if (isPublishedMedia && isVideoUpdateSubmitRef.current) {
        // Edit -> Sync -> Update flow on already-published media: push the
        // synced/edited fields back to the platform via socialpublish.update_video.
        isVideoUpdateSubmitRef.current = false;
        publishPayload.published_id = initialdata?.routing_id;
        // The thumbnail wasn't touched during this edit session (same thumb_id
        // as right after sync) - don't send it, so the platform's existing
        // thumbnail isn't needlessly re-uploaded/reset on every unrelated update.
        let thumbUnchangedSinceSync = false;
        if (publishSyncSnapshotRef.current) {
          try {
            const synced = JSON.parse(publishSyncSnapshotRef.current);
            thumbUnchangedSinceSync = synced?.thumbId === values.thumbId;
          } catch {
            thumbUnchangedSinceSync = false;
          }
        }
        if (thumbUnchangedSinceSync) {
          delete publishPayload.thumb;
          delete publishPayload.thumb_id;
        }
        dispatch(
          updateVideoPublishMedia(publishPayload, (resp: any) => {
            setIsUpdatingVideo(false);
            if (resp?.status === 200) {
              // Success - drop back to the locked "Edit" state and close the
              // sheet, same as a fresh publish does via onPublished.
              publishSyncSnapshotRef.current = null;
              clipFieldValuesSyncSnapshotRef.current = null;
              setPublishEditStage("edit");
              onPublished?.(initialdata.id, {
                status: initialdata?.status,
                msg: resp?.data?.message,
                updated: true,
              });
            }
            // On failure, stay on "update" (already-synced, unlocked form) so
            // the user can retry without re-triggering Sync - updateVideoPublishMedia
            // already raised the failure toast.
          }) as any
        );
      } else if (onStageDraft && !isRetryAction) {
        // Embedded (Media Details) flow: never hit the API on card Save. Stage the
        // edit locally and mark it dirty; the media form's Save & Update flushes it
        // via updatePublishMedia (see publishStaged).
        // Retry (a failed, already-submitted destination) is excluded from this
        // staging even in the embedded flow - it re-attempts a call that already
        // happened once, so it fires immediately rather than waiting on the
        // page's own Save & Publish, and (per the direct-call branch below) does
        // NOT mark the page dirty either.
        onStageDraft(initialdata.id, publishPayload);
      } else {
        // Non-embedded page, editing an existing (draft/queued) destination -
        // or, in the embedded flow, a Retry on a failed destination (see
        // above): wait for the RPC before deciding anything. On success,
        // close the sheet via onPublished (same as a fresh publish/update) -
        // the card's own content still only ever comes from
        // currentMediaDetails.socials. On failure, leave the sheet open so
        // the user can fix and retry; updatePublishMedia already raised the
        // failure toast.
        updatePublishMedia(publishPayload, (resp: any) => {
          if (resp?.status !== 200) return; // failure toast already raised
          // handlePublished treats a negative `status` as a failure and bails
          // out before closing the sheet - forwarding initialdata.status here
          // would replay a Retry's pre-update FAILED status (-1/-2) on its
          // own success callback, making a successful retry look like a
          // fresh failure and leaving the sheet stuck open. This callback
          // only ever runs once the RPC above actually succeeded, so report
          // that success plainly instead of the stale prior record status.
          onPublished?.(initialdata.id, {
            status: SOCIAL_PUBLISH_STATUS.COMPLETED,
            msg: resp?.data?.message,
            updated: true,
          });
        });
      }
    }
  };
  const submitValuesRef = useRef(submitValues);
  submitValuesRef.current = submitValues;

  useEffect(() => {
    if (
      initialdata?.preVideo &&
      initialdata.preVideo !== formik.values.preVideo
    ) {
      formik.setFieldValue("preVideo", initialdata.preVideo);
    }
  }, [initialdata?.preVideo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      initialdata?.postVideo &&
      initialdata.postVideo !== formik.values.postVideo
    ) {
      formik.setFieldValue("postVideo", initialdata.postVideo);
    }
  }, [initialdata?.postVideo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const rawOrientation = currentMediaDetails?.orientation;
    if (!rawOrientation || hasExistingSocialRecord) return;
    const ch = socialChannelForCard;
    if (!ch) return;
    const orientationKey =
      rawOrientation === 2
        ? "portrait"
        : rawOrientation === 3
          ? "square"
          : "landscape";
    const resolveSlate = (key: "pre" | "post"): string => {
      const orientationSlate = ch[`${orientationKey}_slate`] as
        | PrePost
        | undefined;
      if (orientationSlate?.[key]) return orientationSlate[key];
      const flat = ch[key];
      return typeof flat === "string" ? flat : "";
    };
    const newPre = resolveSlate("pre");
    const newPost = resolveSlate("post");
    if (newPre && !formik.values.preVideo) {
      formik.setFieldValue("preVideo", newPre);
    }
    if (newPost && !formik.values.postVideo) {
      formik.setFieldValue("postVideo", newPost);
    }
  }, [currentMediaDetails?.orientation, socialChannelForCard]);

  useEffect(() => {
    const preId = formik.values.preVideo;
    const postId = formik.values.postVideo;
    const ids = [preId, postId].filter(Boolean) as string[];
    if (ids.length === 0) return;
    dispatch(
      getListMedia({
        id: ids,
        asset_type: "slates",
        key: "secondary",
      } as any) as any
    );
  }, [formik.values.preVideo, formik.values.postVideo]); // eslint-disable-line react-hooks/exhaustive-deps

  const formikSlateRef = useRef(formik.values);
  formikSlateRef.current = formik.values;

  useEffect(() => {
    if (!Array.isArray(slateMediaList)) return;
    const preId = formikSlateRef.current.preVideo;
    const postId = formikSlateRef.current.postVideo;

    if (preId) {
      const preMedia = slateMediaList.find((m: any) => m.id === preId);
      if (preMedia) {
        setSelectedPreSlate({
          label: preMedia.id,
          value: preMedia.title,
          image: preMedia.images?.thumb
            ? getThumbnailUrlUtil({
                media: preMedia,
                id: (preMedia as any)?.is_vertical
                  ? preMedia?.images?.portrait
                  : preMedia?.images?.thumb,
                source: preMedia?.source,
              })
            : "",
          duration: secondsToHms((preMedia as any).meta?.duration || 0),
          thumb: !!generateBaseUrl(preMedia as any),
        });
      }
    } else {
      setSelectedPreSlate(null);
    }

    if (postId) {
      const postMedia = slateMediaList.find((m: any) => m.id === postId);
      if (postMedia) {
        setSelectedPostSlate({
          label: postMedia.id,
          value: postMedia.title,
          image: postMedia.images?.thumb
            ? getThumbnailUrlUtil({
                media: postMedia,
                id: (postMedia as any)?.is_vertical
                  ? postMedia?.images?.portrait
                  : postMedia?.images?.thumb,
                source: postMedia?.source,
              })
            : "",
          duration: secondsToHms((postMedia as any).meta?.duration || 0),
          thumb: !!generateBaseUrl(postMedia as any),
        });
      }
    } else {
      setSelectedPostSlate(null);
    }
  }, [slateMediaList]);

  const handleSlateClose = (key: "pre" | "post") => {
    if (key === "pre") {
      formik.setFieldValue("preVideo", "");
      setSelectedPreSlate(null);
    } else {
      formik.setFieldValue("postVideo", "");
      setSelectedPostSlate(null);
    }
  };

  const handleSlateEdit = (key: "pre" | "post") => {
    if (key === "pre") {
      setTimeout(() => preSlateInputRef.current?.openMenu(), 0);
      setIsPreSlateEdit(true);
    } else {
      setTimeout(() => postSlateInputRef.current?.openMenu(), 0);
      setIsPostSlateEdit(true);
    }
  };

  const {
    activeField,
    setActiveField,
    customPrompt,
    handleCustomPromptClick,
    handleCustomPromptClose,
    handleCustomPromptTextChange,
    handlePromptSend,
    titleHistory,
    setTitleHistory,
    currentTitleHistoryIndex,
    setCurrentTitleHistoryIndex,
    isTitleBlurLoading,
    setIsTitleBlurLoading,
    setIsDescriptionBlurLoading,
    setIsTitleAiButtonLoading,
    handleTitleHistoryNavigation,
    handleTitleBlur,
    descriptionHistory,
    setDescriptionHistory,
    currentDescriptionHistoryIndex,
    setCurrentDescriptionHistoryIndex,
    isDescriptionBlurLoading,
    setIsDescriptionAiButtonLoading,
    handleDescriptionHistoryNavigation,
    handleDescriptionBlur,
  } = usePublishCardAiFields({
    formik,
    initialdata,
    currentMediaDetails,
    isWizard,
    metaPresetData,
  });

  // Duration validation
  useEffect(() => {
    if (
      expandedId === initialdata.id &&
      currentMediaDetails?.meta?.duration &&
      config.validateDuration
    ) {
      const durationInSeconds = currentMediaDetails.meta.duration;
      const validation = config.validateDuration(durationInSeconds);
      if (!validation.valid) {
        setDurationError(validation.error || null);
      } else {
        setDurationError(null);
      }
    } else {
      setDurationError(null);
    }
  }, [expandedId, initialdata.id, currentMediaDetails?.meta?.duration, config]);

  // Update form values when initialdata changes
  useEffect(() => {
    const socialTitle = initialdata?.social_title;
    const socialDesc = initialdata?.social_desc;
    const isEditableEntry =
      initialdata?.isNew === 100 ||
      initialdata?.status === SOCIAL_PUBLISH_STATUS.QUEUE ||
      initialdata?.status === SOCIAL_PUBLISH_STATUS.DRAFT;
    const affixLocked = !isEditableEntry;
    const canSync = (field: "title" | "desc" | "tags") =>
      !isCardSavedRef.current && !editedFieldsRef.current[field];
    if (initialdata?.title != null && canSync("title")) {
      const titleSuffix = affixLocked
        ? ""
        : (socialData != null && socialData?.isFromPreset === true
            ? socialData?.title_append || ""
            : initialdata?.title_suffix ||
              `${socialTitle ? "| " + socialTitle : ""}`
          ).trim();
      const titlePrefix = affixLocked
        ? ""
        : (socialData?.title_prepend || initialdata?.title_prefix || "").trim();
      const newTitle = affixLocked
        ? initialdata?.title || ""
        : composeAffixed(initialdata?.title || "", titlePrefix, titleSuffix);
      if (formik.values.title !== newTitle) {
        formik.setFieldValue("title", newTitle);
      }
    }
    // if (initialdata?.desc != null) {
    if (canSync("desc")) {
      const descSuffix = affixLocked
        ? ""
        : (socialData != null && socialData?.isFromPreset === true
            ? socialData?.description_append || ""
            : initialdata?.description_suffix ||
              `${socialDesc ? socialDesc : ""}`
          ).trim();
      const descPrefix = affixLocked
        ? ""
        : (
            socialData?.description_prepend ||
            initialdata?.description_prefix ||
            ""
          ).trim();
      const newDesc = affixLocked
        ? initialdata?.desc || ""
        : composeAffixed(initialdata?.desc || "", descPrefix, descSuffix);
      if (formik.values.desc !== newDesc) {
        formik.setFieldValue("desc", newDesc);
      }
    }
    // }
    // if (
    //   initialdata?.tags &&
    //   JSON.stringify(formik.values.tags) !== JSON.stringify(initialdata.tags)
    // ) {
    if (canSync("tags")) {
      const socialTags = Array.isArray(initialdata?.social_tags)
        ? initialdata?.social_tags
        : [];
      if (socialData != null && socialData?.isFromPreset === true) {
        const tagsMode = socialData?.tags_mode;
        const currentTags: string[] = Array.isArray(initialdata?.tags)
          ? initialdata?.tags
          : [];
        const incomingTags: string[] = Array.isArray(socialData?.tags)
          ? socialData?.tags
          : [];
        formik.setFieldValue(
          "tags",
          resolveTagsByMode({
            mode: tagsMode,
            currentTags,
            incomingTags,
            defaultMode: PRESET_MODES.OVERRIDE,
          })
        );
      } else {
        const mergedTags = Array.isArray(initialdata?.tags)
          ? initialdata.tags
          : [];
        formik.setFieldValue("tags", [
          ...new Set([...mergedTags, ...socialTags]),
        ]);
      }
    }
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialdata?.title,
    initialdata?.desc,
    initialdata?.tags,
    initialdata?.social_title,
    initialdata?.status,
    initialdata?.isNew,
    socialData,
  ]);

  useEffect(() => {
    const canSyncThumb =
      !isCardSavedRef.current && !editedFieldsRef.current.thumbnail;
    if (!canSyncThumb) return;
    if (initialdata?.thumb == null && initialdata?.thumb_id == null) return;
    if (
      initialdata?.thumb != null &&
      formik.values.thumb !== initialdata.thumb
    ) {
      formik.setFieldValue("thumb", initialdata.thumb);
    }
    if (
      initialdata?.thumb_id != null &&
      formik.values.thumbId !== initialdata.thumb_id
    ) {
      formik.setFieldValue("thumbId", initialdata.thumb_id);
    }
  }, [initialdata?.thumb, initialdata?.thumb_id]);

  // Reopening this destination later (published or draft) should reflect
  // whichever mode it was actually created from - initialdata can update in
  // place (socket/redux sync) for a card that stays mounted, so re-sync here
  // rather than relying solely on the initial useState value above.
  useEffect(() => {
    const publishType = initialdata?.publish_type;
    if (publishType && publishType !== videoRangeMode) {
      setVideoRangeMode(publishType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialdata?.publish_type]);

  // A fresh repeat-attempt card's initial mode (set above) is resolved before
  // this media's clips have necessarily loaded (availableClips starts empty
  // until getListChapters resolves) - re-resolve once they arrive so "Clips"
  // is picked whenever an unused clip actually exists, not just "Segment" by
  // default. Skipped once the user has manually touched the picker.
  useEffect(() => {
    if (videoRangeModeTouchedRef.current) return;
    if (initialdata?.isNew !== 100 || channelHistory.records.length === 0) {
      return;
    }
    const nextMode = resolveNextPublishMode(
      channelHistory,
      availableClips.map((c) => c.id)
    );
    if (nextMode !== videoRangeMode) {
      setVideoRangeMode(nextMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableClips, channelHistory]);

  // Persist edited field VALUES back to the parent publish object so they survive
  // the card unmounting when the sheet closes. On remount formik re-seeds from
  // initialdata; without this the user's typed value would be lost (only the
  // "don't re-sync" flag was persisted, so the stale initialdata value stuck).
  useEffect(() => {
    if (!onPersistEditedValues) return;
    const edited = editedFieldsRef.current;
    if (!edited.title && !edited.desc && !edited.tags && !edited.thumbnail) {
      return;
    }
    const patch: Record<string, any> = {};
    if (edited.title) patch.title = formik.values.title;
    if (edited.desc) patch.desc = formik.values.desc;
    if (edited.tags) patch.tags = formik.values.tags;
    if (edited.thumbnail) {
      patch.thumb = formik.values.thumb;
      patch.thumb_id = formik.values.thumbId;
    }
    onPersistEditedValues(initialdata?.id, patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.title,
    formik.values.desc,
    formik.values.tags,
    formik.values.thumb,
    formik.values.thumbId,
  ]);

  useEffect(() => {
    if (!onPersistEditedValues) return;
    if (isAlreadyPublished) return;
    onPersistEditedValues(initialdata?.id, {
      _markActive: markActive,
      status: markActive
        ? SOCIAL_PUBLISH_STATUS.QUEUE
        : SOCIAL_PUBLISH_STATUS.DRAFT,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markActive]);

  const {
    handleThumbnailChange,
    thumbnailDimensionError,
    setThumbnailDimensionError,
  } = useThumbnailSelection({
    formik,
    currentMediaDetails,
    assets,
    mediaBaseUrl,
    platformType,
    canSyncThumbnail: () =>
      !isCardSavedRef.current && !editedFieldsRef.current.thumbnail,
  });

  // When is_sftp=0 for YouTube, auto-set first monetization option
  useEffect(() => {
    if (isSftp) return;
    if (!monetizationOptions || monetizationOptions.length === 0) return;
    const first = monetizationOptions[0];
    if (formik.values?.monetize_policy_id !== first.id) {
      formik.setFieldValue("monetize_policy_id", first.id);
      formik.setFieldValue("monetize_policy_name", first.title);
    }
  }, [isSftp, monetizationOptions]);

  useEffect(() => {
    if (platformType !== "youtube") return;

    const policyId = formik.values?.monetize_policy_id;
    if (!policyId) return;
    if (!monetizationOptions || monetizationOptions.length === 0) return;

    const matched = monetizationOptions.find((opt) => opt.id === policyId);
    const matchedTitle = matched?.title ?? "";
    if (!matchedTitle) return;

    if (formik.values?.monetize_policy_name !== matchedTitle) {
      formik.setFieldValue("monetize_policy_name", matchedTitle);
    }
  }, [
    platformType,
    formik.values?.monetize_policy_id,
    formik.values?.monetize_policy_name,
    monetizationOptions,
  ]);

  // Track the last fetched channel_id to prevent duplicate calls within this component
  const lastFetchedChannelIdRef = useRef<string | null>(null);
  const lastFetchedPlaylistChannelIdRef = useRef<string | null>(null);
  // Store getYoutubePlaylist in a ref to prevent it from triggering re-renders
  const getYoutubePlaylistRef = useRef(getYoutubePlaylist);

  // Update ref when getYoutubePlaylist changes
  useEffect(() => {
    getYoutubePlaylistRef.current = getYoutubePlaylist;
  }, [getYoutubePlaylist]);

  useEffect(() => {
    if (platformType !== "youtube") return;

    const channelId = initialdata?.channel_id ?? initialdata?.id ?? "";
    if (!channelId || lastFetchedChannelIdRef.current === channelId) return;

    let isActive = true;
    lastFetchedChannelIdRef.current = channelId;

    let request = inFlightMonetization.get(channelId);
    if (!request) {
      request = GetMonetizationOptionsAPI(channelId)
        .then((res) => (res && res.status === 200 ? res.data : null))
        .finally(() => {
          inFlightMonetization.delete(channelId);
        });
      inFlightMonetization.set(channelId, request);
    }

    request
      .then((data) => {
        if (isActive && data) {
          setMonetizationOptions(data);
        }
      })
      .catch((error) => {
        console.error("Error fetching monetization options:", error);
        if (isActive) lastFetchedChannelIdRef.current = null;
      });

    return () => {
      isActive = false;
    };
  }, [platformType, initialdata?.channel_id, initialdata?.id]);

  // Get YouTube playlist
  useEffect(() => {
    if (platformType === "youtube" && getYoutubePlaylistRef.current) {
      const channelId = initialdata?.channel_id ?? initialdata?.id ?? "";

      // Prevent duplicate calls using module-level cache and component-level ref
      if (
        !channelId ||
        lastFetchedPlaylistChannelIdRef.current === channelId ||
        fetchingCache.playlist.has(channelId)
      ) {
        return;
      }

      const fetchPlaylist = async () => {
        fetchingCache.playlist.add(channelId);
        try {
          await getYoutubePlaylistRef.current(channelId);
          lastFetchedPlaylistChannelIdRef.current = channelId;
        } catch (error) {
          console.error("Error fetching YouTube playlist:", error);
        } finally {
          fetchingCache.playlist.delete(channelId);
        }
      };

      fetchPlaylist();
    }
  }, [
    platformType,
    initialdata?.channel_id,
    initialdata?.id,
    // Removed getYoutubePlaylist from dependencies - using ref instead
  ]);

  // Add styles for rotating animation
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = rotatingStyles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Handle scheduler
  useEffect(() => {
    if (scheduled) {
      formik.setFieldValue("privacy", "schedule");
      formik.setFieldValue("schedule", scheduled);
      setShowScheduler(true);
    }
  }, [scheduled]);

  useEffect(() => {
    if (!isPreset) {
      return;
    }
    if (formik.values.schedule_after && formik.values.schedule_after > 0) {
      setShowScheduler(true);
    }
  }, [formik.values.schedule_after, isPreset]);

  // Preset validation
  useEffect(() => {
    const handlePresetValidation = () => {
      if (isPreset) {
        Object.keys(formik.initialValues).forEach((field) => {
          formik.setFieldTouched(
            field as keyof typeof formik.initialValues,
            true
          );
        });
        shouldValidateTagsRef.current = true;
        formik.validateForm().then((errors) => {
          const isValid = Object.keys(errors).length === 0;
          if (platformType !== "youtube") {
            scrollToFirstError(errors);
          }
          const validationEvent = new CustomEvent("validationResult", {
            detail: { isValid, errors, formId: initialdata.id },
          });
          window.dispatchEvent(validationEvent);
        });
      }
    };

    window.addEventListener("validatePresetForms", handlePresetValidation);
    return () => {
      window.removeEventListener("validatePresetForms", handlePresetValidation);
    };
  }, [isPreset, formik, initialdata.id, platformType]);

  // Track previous payload to prevent unnecessary updates
  const previousPayloadRef = useRef<string>("");

  // Auto-enable YouTube-specific fields when they have values
  useEffect(() => {
    if (platformType === "youtube") {
      setEnabledFields((prev) => {
        const updated: Record<string, boolean> = { ...prev };
        const youtubeFields = [
          "category",
          "monetize_policy_id",
          "playlist",
          "is_created_for_kids",
          "is_rights_managements_allowed",
        ];

        youtubeFields.forEach((field) => {
          const value = formik.values[field as keyof typeof formik.values];
          if (value !== undefined && value !== null && value !== "") {
            // For playlist, check if it's a non-empty string (comma-separated) or array
            if (field === "playlist") {
              if (typeof value === "string") {
                const playlistArray = value.split(",").filter(Boolean);
                updated[field] = playlistArray.length > 0;
              } else if (Array.isArray(value)) {
                updated[field] = value.length > 0;
              } else {
                updated[field] = false;
              }
            } else {
              updated[field] = true;
            }
          } else {
            // If value is empty, keep the current state (don't force disable if it was enabled)
            // This allows manual toggling via checkbox
          }
        });

        return updated;
      });
    }
  }, [
    platformType,
    formik.values.category,
    formik.values.monetize_policy_id,
    formik.values.playlist,
    formik.values.is_created_for_kids,
    formik.values.is_rights_managements_allowed,
  ]);

  // Handle preset payload updates
  useEffect(() => {
    if (isPreset && handleAllSelectedPresets) {
      const payload = getEnabledFieldsPayload(formik.values);
      if (formik.values.title_ai_checked) {
        payload.title_ai_type =
          formik.values.title_ai_type || socialData?.title_ai_type || "";
        payload.title_ai_value =
          formik.values.title_ai_value || socialData?.title_ai_value || "";
      } else {
        payload.title_ai_type = "";
        payload.title_ai_value = "";
      }
      if (formik.values.desc_ai_checked) {
        payload.desc_ai_type =
          formik.values.desc_ai_type || socialData?.desc_ai_type || "";
        payload.desc_ai_value =
          formik.values.desc_ai_value || socialData?.desc_ai_value || "";
      } else {
        payload.desc_ai_type = "";
        payload.desc_ai_value = "";
      }

      // Create a stable string representation of the payload to compare
      const payloadString = JSON.stringify(payload);

      // Only call handleAllSelectedPresets if the payload actually changed
      if (previousPayloadRef.current !== payloadString) {
        previousPayloadRef.current = payloadString;
        handleAllSelectedPresets(payload as any);
      }
    }
  }, [
    enabledFields,
    formik.values.title,
    formik.values.desc,
    formik.values.tags,
    formik.values.privacy,
    formik.values.schedule_after,
    formik.values.title_ai_checked,
    formik.values.title_ai_type,
    formik.values.title_ai_value,
    formik.values.desc_ai_checked,
    formik.values.desc_ai_type,
    formik.values.desc_ai_value,
    formik.values.channel_name,
    formik.values.id,
    formik.values.category,
    formik.values.monetize_policy_id,
    formik.values.playlist,
    formik.values.is_created_for_kids,
    formik.values.is_rights_managements_allowed,
    socialData,
    isPreset,
    handleAllSelectedPresets,
    getEnabledFieldsPayload,
  ]);

  const handleImageError = () => {
    setIsValidImage(false);
  };

  const handleFieldToggle = (fieldName: string, checked: boolean) => {
    setEnabledFields((prev) => ({
      ...prev,
      [fieldName]: checked,
    }));
  };

  const handleShowMore = () => {
    // In the Offcanvas sheet the card is always expanded (the sheet is the open/close affordance),
    // so a stray header click must not collapse it into an empty sheet.
    if (inSheet) return;
    onToggleExpand(initialdata.id);
  };

  // Validate single social card
  const validateSingleSocial = useCallback(() => {
    // Only validate if status is 0 (draft/pending) or card is new (isNew === 100)
    if (initialdata.status !== 0 && initialdata.isNew !== 100) {
      return true;
    }

    let isValidationFailed = false;
    const titleLength = formik.values.title?.length || 0;
    const titleLimit = config.titleCharacterLimit;
    if (titleLength > titleLimit) {
      formik.setFieldTouched("title", true);
      formik.setFieldError(
        "title",
        `Title must be less than ${titleLimit} characters`
      );
      if (!isValidationFailed) {
        isValidationFailed = true;
      }
    }

    // Check if desc length is greater than 5000
    const descLength = formik.values.desc?.length || 0;
    if (descLength > 5000) {
      formik.setFieldTouched("desc", true);
      formik.setFieldError(
        "desc",
        "Description must be less than 5000 characters"
      );
      if (!isValidationFailed) {
        isValidationFailed = true;
      }
    }

    // Check if tags length is greater than 500

    if (formik.values.tags && Array.isArray(formik.values.tags)) {
      const { tagCount } = calculateTagCount(
        formik.values.tags,
        platformType === "youtube"
      );
      if (tagCount > 500) {
        formik.setFieldTouched("tags", true);
        formik.setFieldError("tags", "Tags must be less than 500 characters");
        if (!isValidationFailed) {
          isValidationFailed = true;
        }
      }

      if (
        platformType === "youtube" &&
        !areYoutubeSocialTagsCharLengthValid(formik.values.tags)
      ) {
        formik.setFieldTouched("tags", true);
        formik.setFieldError("tags", YOUTUBE_SOCIAL_TAG_LENGTH_ERROR_MESSAGE);
        if (!isValidationFailed) {
          isValidationFailed = true;
        }
      }
    }

    if (isValidationFailed) {
      return false;
    }
    return true;
  }, [
    formik.values.title,
    formik.values.desc,
    formik.values.tags,
    initialdata?.status,
    initialdata?.isNew,
    platformType,
  ]);

  const isSocialValid = useMemo(
    () => validateSingleSocial(),
    [validateSingleSocial]
  );

  // Save: stage the destination as a draft (status 12) with NO validation gate.
  // The user can fill mandatory fields later and click Publish.
  const handleSave = () => {
    submitModeRef.current = "save";
    setMarkActive(false);
    submitValues(formik.values);
  };

  // Publish: validate all mandatory fields; only queue (status 0) when valid.
  // On failure, surface field errors and keep the card open — do not change status.
  const handlePublish = () => {
    submitModeRef.current = "publish";

    // Clips mode's per-clip fields (title/desc/category/kids/visibility/
    // thumbnail) live in clipFieldValues, not formik, so formik's own
    // validateForm() below never checks them - validate every selected
    // clip's required fields here first.
    if (
      videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS &&
      selectedClipIds.length > 0
    ) {
      setClipFieldsTouched(true);
      const missingClip = selectedClipIds.find((clipId) => {
        const cv = clipFieldValues[clipId];
        if (!cv) return true;
        if (
          !cv.title?.trim() ||
          !cv.desc?.trim() ||
          !cv.thumbId ||
          !cv.tags?.length
        ) {
          return true;
        }
        if (
          platformType === "youtube" &&
          (!cv.category || !cv.is_created_for_kids || !cv.privacy)
        ) {
          return true;
        }
        if (
          isSftp &&
          (!cv.monetize_policy_id || !cv.is_rights_managements_allowed)
        ) {
          return true;
        }
        return false;
      });
      if (missingClip) {
        const clip = availableClips.find((c) => c.id === missingClip);
        toast.fail(
          `${clip?.title || "A selected clip"}: fill in every required field before publishing.`
        );
        setActiveClipTabId(missingClip);
        isVideoUpdateSubmitRef.current = false;
        setIsUpdatingVideo(false);
        return;
      }
      // Every selected clip already passed its own required-field check
      // above - the rest of this function (and formik.handleSubmit's own
      // internal validationSchema check, which runs regardless of whether
      // validateForm() is called first) validates formik's TOP-LEVEL values
      // (title/desc/tags/category/thumb/duration), which belong to
      // Full/Segment mode and are largely irrelevant placeholders here
      // (Clips mode edits clipFieldValues, not formik.values). Call
      // submitValues directly (same as handleSave already does) instead of
      // going through formik.handleSubmit(), so Formik's schema validation
      // can't silently swallow this submit on a blank/stale top-level field
      // the user never touches in this mode.
      setMarkActive(true);
      submitValues(formik.values);
      return;
    }

    Object.keys(formik.initialValues).forEach((field) => {
      formik.setFieldTouched(field as keyof typeof formik.initialValues, true);
    });
    shouldValidateTagsRef.current = true;

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        isVideoUpdateSubmitRef.current = false;
        setIsUpdatingVideo(false);
        scrollToFirstError(errors);
        return;
      }

      if (durationError) {
        isVideoUpdateSubmitRef.current = false;
        setIsUpdatingVideo(false);
        toast.fail(durationError);
        return;
      }

      setMarkActive(true);

      if (
        platformType === "youtube" &&
        formik.values.thumb &&
        mediaOrientation !== "portrait"
      ) {
        const originalUrl = formik.values.thumb.replace("_180.jpg", ".jpg");
        checkYoutubeThumbnailDimensions(originalUrl).then((dimensionError) => {
          setThumbnailDimensionError(dimensionError);
          if (dimensionError) {
            isVideoUpdateSubmitRef.current = false;
            setIsUpdatingVideo(false);
            return;
          }
          formik.handleSubmit();
        });
        return;
      }

      formik.handleSubmit();
    });
  };

  const buildStagePayload = useCallback(() => {
    const values = {
      ...formik.values,
      title: formik.values.title?.trim() ?? formik.values.title,
      desc: formik.values.desc?.trim() ?? formik.values.desc,
    };
    const publishPayload = config.buildPayload(
      values,
      initialdata,
      enabledFields
    );
    const hasOrientation =
      !!currentMediaDetails?.orientation &&
      currentMediaDetails.orientation !== 0;
    if (hasOrientation && values.preVideo && values.preVideo.length > 0) {
      publishPayload.preVideo = values.preVideo;
    }
    if (hasOrientation && values.postVideo && values.postVideo.length > 0) {
      publishPayload.postVideo = values.postVideo;
    }
    return publishPayload;
  }, [
    formik.values,
    config,
    initialdata,
    enabledFields,
    currentMediaDetails?.orientation,
  ]);

  useEffect(() => {
    if (!autoStage || !onStageDraft) return;
    if (initialdata?.isNew !== 100) return;
    const t = setTimeout(() => {
      onStageDraft(initialdata.id, buildStagePayload(), { silent: true });
    }, 400);
    return () => clearTimeout(t);
  }, [
    autoStage,
    onStageDraft,
    initialdata?.isNew,
    initialdata?.id,
    buildStagePayload,
  ]);

  useEffect(() => {
    const rules = socialData?.conditional_rules;
    if (rules && rules?.length > 0) {
      for (const rule of rules) {
        if (
          rule.field === "language" &&
          rule.value === currentMediaDetails?.lang
        ) {
          if (rule.actions?.category !== "original") {
            formik.setFieldValue("category", rule.actions?.category);
          }
          if (rule.actions?.monetize_policy_name !== "original") {
            if (rule.actions?.monetize_policy_id) {
              formik.setFieldValue(
                "monetize_policy_id",
                rule.actions?.monetize_policy_id
              );
            }
            if (rule.actions?.monetize_policy_name) {
              formik.setFieldValue(
                "monetize_policy_name",
                rule.actions?.monetize_policy_name
              );
            }
          }
          if (rule.actions?.playlist !== "original") {
            formik.setFieldValue("playlist", rule.actions?.playlist);
          }
          break;
        } else if (
          rule.field === "ctype" &&
          rule.value === currentMediaDetails?.ctype
        ) {
          if (rule.actions?.category !== "original") {
            formik.setFieldValue("category", rule.actions?.category);
          }
          if (rule.actions?.monetize_policy_name !== "original") {
            if (rule.actions?.monetize_policy_id) {
              formik.setFieldValue(
                "monetize_policy_id",
                rule.actions?.monetize_policy_id
              );
            }
            if (rule.actions?.monetize_policy_name) {
              formik.setFieldValue(
                "monetize_policy_name",
                rule.actions?.monetize_policy_name
              );
            }
          }
          if (rule.actions?.playlist !== "original") {
            formik.setFieldValue("playlist", rule.actions?.playlist);
          }
          break;
        }
      }
    }
  }, [socialData, currentMediaDetails]);

  const handleDateTimeSelected = (timestamp: number) => {
    setScheduled(timestamp);
    formik.setFieldValue("schedule", timestamp);
    formik.setFieldValue("privacy", "schedule");
  };

  const handleTitleAiTypeChange = (payload: any) => {
    formik.setFieldValue("title_ai_type", payload.key);
    formik.setFieldValue("title_ai_value", payload.value);
  };

  const handleDescAiTypeChange = (payload: any) => {
    formik.setFieldValue("desc_ai_type", payload.key);
    formik.setFieldValue("desc_ai_value", payload.value);
  };

  const renderStatusIndicator = (status: number) => {
    if ([2, 3, 4, 5, 7].includes(status)) {
      return (
        <Tooltip
          content={socialPublishStatusTooltipText(status)}
          placement="top"
        >
          <div className="d-flex align-items-center gap-2">
            <HourglassIcon className="rotating-hourglass" />
            <span
              className={`py-1 px-2 badge ${
                socialPublishStatusToTextAndColor(status).statusColor
              }`}
            >
              {socialPublishStatusToTextAndColor(status).statusText}
            </span>
          </div>
        </Tooltip>
      );
    }
    if (formik.values.isNew !== 100 && initialdata.status !== 10) {
      return (
        <Tooltip
          content={
            initialdata?.status === -2 || initialdata?.status === -1
              ? (initialdata?.msg ??
                `${socialPublishStatusTooltipText(status)} ${
                  initialdata?.job_type
                }`)
              : initialdata?.status === 0
                ? (initialdata?.msg ??
                  socialPublishStatusTooltipText(initialdata?.status))
                : ""
          }
          placement="top"
        >
          <span
            className={`py-1 px-2 badge ${
              socialPublishStatusToTextAndColor(status).statusColor
            }`}
          >
            {socialPublishStatusToTextAndColor(status).statusText}
          </span>
        </Tooltip>
      );
    }
    return null;
  };

  // Memoize platform icon to avoid recalculation on every render
  const platformIcon = useMemo(() => {
    if (typeof config.icon === "string") {
      return (
        <i
          className={`bi ${config.icon} fw-bold fs-5 position-absolute px-1`}
          style={{
            bottom: -2,
            right: -2,
            backgroundColor: "white",
            borderRadius: "2px 0 0 0",
            color: config.iconColor,
            padding: "2px",
          }}
        ></i>
      );
    }
    // If it's a React component (like DailymotionIcon), render it
    if (
      typeof config.icon === "function" ||
      (config.icon as any).prototype?.isReactComponent
    ) {
      const IconComponent = config.icon as React.ComponentType<any>;
      return (
        <IconComponent
          width={16}
          height={16}
          className="position-absolute"
          style={{
            bottom: -2,
            right: -2,
            backgroundColor: "white",
            borderRadius: "2px 0 0 0",
            color: config.iconColor,
            padding: "2px",
          }}
        />
      );
    }
    // If it's already a ReactElement, return it as is
    return config.icon as React.ReactElement;
  }, [config.icon, config.iconColor]);

  // Memoize image URL to avoid recalculation on every render
  const imageUrl = useMemo(() => {
    if (platformType === "facebook") {
      return isValidImage
        ? `https://graph.facebook.com/${realChannelId}/picture?type=large`
        : noImage;
    }
    return isValidImage ? initialdata.image || noImage : noImage;
  }, [platformType, isValidImage, realChannelId, initialdata.image]);

  const countVisibleCharactersOnX = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex) || [];
    const urlCharCount = urls.length * 23;
    const textWithoutUrls = text.replace(urlRegex, "");
    const visibleLength = countGraphemes(textWithoutUrls);
    return visibleLength + urlCharCount;
  };

  // Memoize visible character count for Twitter to avoid recalculation on every render
  const visibleCharCount = useMemo(
    () => countVisibleCharactersOnX(formik.values?.title ?? ""),
    [formik.values?.title]
  );

  return (
    <div
      className={inSheet ? "socialCard socialSheet" : "socialCard"}
      style={{
        border: expandedId === initialdata.id ? "1px solid #DDDDDD" : "none",
        borderRadius: expandedId === initialdata.id ? "4px" : "0px",
      }}
    >
      <div className="row bg-light m-0 pointer" onClick={handleShowMore}>
        <div className="d-flex justify-content-between align-items-center w-100 m-0">
          <div className="d-flex gap-2 my-2 align-items-center">
            <div className="position-relative">
              <img
                src={imageUrl}
                alt=""
                height="50px"
                width="50px"
                className="rounded border"
                onError={handleImageError}
              />
              {platformType === "dailymotion" ? (
                <DailymotionIcon
                  width={16}
                  height={16}
                  className="position-absolute"
                  style={{
                    bottom: -2,
                    right: -2,
                    backgroundColor: "white",
                    borderRadius: "2px 0 0 0",
                    color: "#000000",
                  }}
                />
              ) : (
                platformIcon
              )}
            </div>
            <div className="d-flex flex-column">
              <span className="fs-5 pb-2">{formik.values.channel_name}</span>
              <span className="fw-light fs-11">{config.description}</span>
              {durationError && (
                <div
                  className="alert alert-danger py-1 px-2 mt-1 mb-0"
                  style={{ fontSize: "11px", lineHeight: "1.2" }}
                >
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>
                  {durationError}
                </div>
              )}
              {!isSocialValid && (
                <Alert
                  variant="danger"
                  className="py-1 px-2 mt-1 mb-0"
                  style={{ fontSize: "11px", lineHeight: "1.2" }}
                >
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>
                  Your content is not valid for publishing. Please fix the
                  errors and try again.
                </Alert>
              )}
            </div>
          </div>
          {!isPreset && (
            <div className="d-flex gap-2">
              {renderStatusIndicator(initialdata?.status)}
              {initialdata?.status === 10 && (
                <div
                  className="d-flex gap-2 align-items-center"
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className={`mb-1 ${
                      initialdata?.schedule &&
                      initialdata?.schedule >= Date.now()
                        ? ""
                        : socialPublishStatusToTextAndColor(initialdata?.status)
                            .statusColor
                    }`}
                    style={{
                      borderRadius: "50%",
                      height: "10px",
                      width: "10px",
                    }}
                  >
                    {initialdata?.schedule &&
                      initialdata?.schedule >= Date.now() && (
                        <i className="bi bi-clock"></i>
                      )}
                  </span>
                  <span className="text-uppercase fs-6 cursor-pointer">
                    {initialdata?.schedule
                      ? initialdata?.schedule >= Date.now()
                        ? `Scheduled On: ${new Date(
                            initialdata?.schedule
                          ).toLocaleString()}`
                        : `Published On: ${formatTimestampToAgo(
                            initialdata?.schedule
                          )}`
                      : `Published On: ${formatTimestampToAgo(
                          formik.values.mt || Date.now()
                        )}`}
                  </span>
                  <i
                    className="bi bi-box-arrow-up-right pointer"
                    onClick={() => {
                      initialdata?.status === 10 &&
                        openVideoInNewTab(
                          initialdata?.job_type,
                          initialdata?.routing_id
                        );
                    }}
                  ></i>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {(inSheet || expandedId === initialdata.id) && (
        <div className="row m-0">
          <div className="col col-md-12">
            <div className="fw-medium f-14" style={{ marginBottom: "4px" }}>
              What to publish
            </div>
            <div className="text-muted mb-2" style={{ fontSize: "12px" }}>
              Publish the whole video, one or more generated clips, or a segment
              you trim yourself.
            </div>
            <div className="d-flex gap-2">
              {[
                {
                  mode: PUBLISH_RANGE_TYPE.FULL,
                  icon: "bi-camera-video",
                  label: "Full video",
                  badge: "DEFAULT",
                  desc: channelHistory.hasFullPublish
                    ? "Already published in full on this channel."
                    : `The entire ${secondsToHms(mediaDuration)}, published as-is with slates.`,
                  modeDisabled: channelHistory.hasFullPublish,
                },
                {
                  mode: PUBLISH_RANGE_TYPE.CLIPS,
                  icon: "bi-collection-play",
                  label: "Clips",
                  badge: availableClips.length
                    ? `${availableClips.length} READY`
                    : undefined,
                  desc: "Pick one or more — each becomes its own post.",
                  modeDisabled: false,
                },
                {
                  mode: PUBLISH_RANGE_TYPE.SEGMENT,
                  icon: "bi-sliders",
                  label: "Custom segment",
                  desc: "Trim your own start and end points.",
                  modeDisabled: !isSegmentEligible,
                  disabledReason:
                    "Available once the video has finished processing.",
                },
              ].map(
                ({
                  mode,
                  icon,
                  label,
                  badge,
                  desc,
                  modeDisabled,
                  disabledReason,
                }) => {
                  const isSelected = videoRangeMode === mode;
                  const isCardDisabled = publishRangeLocked || modeDisabled;
                  return (
                    <div
                      key={mode}
                      role="button"
                      aria-disabled={isCardDisabled}
                      title={
                        modeDisabled
                          ? (disabledReason ??
                            "Already published in full on this channel.")
                          : undefined
                      }
                      onClick={() => {
                        if (isCardDisabled) return;
                        videoRangeModeTouchedRef.current = true;
                        setVideoRangeMode(mode);
                      }}
                      className={`flex-fill position-relative border rounded px-2 py-2 ${
                        isSelected ? "border-primary bg-primary-subtle" : ""
                      }`}
                      style={{
                        cursor: isCardDisabled ? "not-allowed" : "pointer",
                        borderWidth: isSelected ? 2 : 1,
                        opacity: modeDisabled ? 0.5 : 1,
                      }}
                    >
                      {badge && (
                        <span
                          className="badge bg-light text-secondary fw-semibold position-absolute"
                          style={{ top: 8, right: 8 }}
                        >
                          {/* {badge} */}
                        </span>
                      )}
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <input
                          type="radio"
                          className="form-check-input m-0"
                          checked={isSelected}
                          disabled={isCardDisabled}
                          readOnly
                        />
                        <span
                          className="fw-medium"
                          style={{ fontSize: "11px" }}
                        >
                          {label}
                        </span>
                        {selectedClipIds.length > 0 &&
                          mode === PUBLISH_RANGE_TYPE.CLIPS && (
                            <span className="fw-semibold">
                              {selectedClipIds.length}
                            </span>
                          )}
                      </div>
                      <div
                        className="text-muted f-10"
                        style={{
                          width: "calc(100% - 24px)",
                          marginLeft: "20px",
                        }}
                      >
                        {desc}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
          {videoRangeMode === PUBLISH_RANGE_TYPE.SEGMENT &&
            isSegmentEligible && (
              <div className="col col-md-12" ref={segmentTrimmerRef}>
                {currentMediaDetails?.id && (
                  <div className="border-start border-end border-bottom p-3 mb-3">
                    <GeneralPlayer
                      ref={generalPlayerRef}
                      id={currentMediaDetails.id}
                      controls={false}
                      startTrim={segmentStart}
                      endTrim={segmentEnd}
                      onTimeUpdate={setSegmentPlayerTime}
                      onPlayStateChange={setIsSegmentPlayerPaused}
                      onImageReel={setSegmentImageReel}
                      videos={[
                        {
                          id: currentMediaDetails.id,
                          title: currentMediaDetails.title || "",
                          duration: mediaDuration,
                        },
                      ]}
                      readOnlyMode={segmentRangeLocked}
                    />
                    <div>
                      <VideoRangeTrimmer
                        posterUrl={formik.values.thumb || noImage}
                        duration={mediaDuration}
                        start={segmentStart}
                        end={segmentEnd}
                        onChange={(start: number, end: number) => {
                          setSegmentStart(start);
                          setSegmentEnd(end);
                        }}
                        disabled={segmentRangeLocked}
                        playerRef={generalPlayerRef}
                        currentTime={segmentPlayerTime}
                        isPaused={isSegmentPlayerPaused}
                        imageReel={segmentImageReel}
                        blockedRanges={blockedSegmentRanges}
                        blockedRangesBuffer={SEGMENT_BUFFER_SECONDS}
                      />
                      {blockedSegmentRanges.length > 0 && (
                        <div
                          className="text-muted mt-2"
                          style={{ fontSize: "11px" }}
                        >
                          {`Shaded ranges were already published to this channel (±${SEGMENT_BUFFER_SECONDS}s buffer) and can't be republished.`}
                        </div>
                      )}
                      <div className="d-flex flex-wrap align-items-center mt-2 text-muted kbd-shortcut-row">
                        <b className="me-1">Shortcuts :</b>
                        <span>
                          <b>1 frame -</b>{" "}
                          <kbd>
                            <ArrowLeft size={11} />
                          </kbd>
                          /
                          <kbd>
                            <ArrowRight size={11} />
                          </kbd>
                        </span>
                        <span>
                          <b>1 sec -</b> <kbd>Shift</kbd>+
                          <kbd>
                            <ArrowLeft size={11} />
                          </kbd>
                          /
                          <kbd>
                            <ArrowRight size={11} />
                          </kbd>
                        </span>
                        <span>
                          <b>10 sec -</b> <kbd>Ctrl</kbd>/<kbd>Option</kbd> +
                          <kbd>
                            <ArrowLeft size={11} />
                          </kbd>
                          /
                          <kbd>
                            <ArrowRight size={11} />
                          </kbd>
                        </span>
                        <span>
                          <b>Jump to Start -</b>{" "}
                          <kbd>
                            <ArrowUp size={11} />
                          </kbd>
                        </span>
                        <span>
                          <b>Jump to End -</b>{" "}
                          <kbd>
                            <ArrowDown size={11} />
                          </kbd>
                        </span>
                        <span>
                          <b>Play/Pause -</b> <kbd>Space</kbd>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          {videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS &&
            (() => {
              const toggleClip = (id: string) => {
                if (publishRangeLocked) return;
                // Directly editing an existing single-clip record is scoped
                // to that one clip - it's already selected/seeded above and
                // stays locked, no swapping to a different clip.
                if (isExistingClipRecord) return;
                if (channelHistory.publishedClipIds.has(id)) return;
                setSelectedClipIds((prev) => {
                  const exists = prev.includes(id);
                  const next = exists
                    ? prev.filter((c) => c !== id)
                    : [...prev, id];
                  if (!exists) {
                    setClipFieldValues((cv) => {
                      if (cv[id]) return cv;
                      // Seed the clip's own auto-generated frame (base +
                      // thumb id, as a .jpg) as the default thumbnail, so the
                      // picker shows what will actually be published unless
                      // the user swaps it - fall back to the main form's
                      // thumbnail only if the clip has no frame of its own.
                      const selectedClip = availableClips.find(
                        (c) => c.id === id
                      );
                      const clipAutoThumb =
                        selectedClip?.base && selectedClip?.thumb
                          ? `${selectedClip.base}/${selectedClip.thumb}.jpg`
                          : "";
                      return {
                        ...cv,
                        [id]: {
                          // Only id/start/end come from the clip itself -
                          // every editable field seeds from the main form,
                          // same as the rest of this record.
                          title: formik.values.title || "",
                          desc: formik.values.desc || "",
                          tags: formik.values.tags || [],
                          category: formik.values.category || "",
                          playlist: formik.values.playlist || "",
                          is_created_for_kids:
                            formik.values.is_created_for_kids || "no",
                          privacy:
                            formik.values.privacy || config.defaultPrivacy,
                          thumb: clipAutoThumb || formik.values.thumb || "",
                          thumbId:
                            selectedClip?.thumb || formik.values.thumbId || "",
                          monetize_policy_id:
                            formik.values.monetize_policy_id || "",
                          monetize_policy_name:
                            formik.values.monetize_policy_name || "",
                          is_rights_managements_allowed:
                            formik.values.is_rights_managements_allowed ||
                            "yes",
                        },
                      };
                    });
                    setActiveClipTabId(id);
                  } else if (activeClipTabId === id) {
                    setActiveClipTabId(next[0] ?? null);
                  }
                  return next;
                });
              };
              return (
                <div className="col col-md-12">
                  {availableClips.length === 0 ? (
                    <div className="text-muted text-center f-12 border-start border-end border-bottom p-3">
                      No generated clips found for this video yet.
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2 border-start border-end border-bottom p-3 mb-3">
                      {availableClips.map((clip) => {
                        const isSelected = selectedClipIds.includes(clip.id);
                        const isClipPublished =
                          channelHistory.publishedClipIds.has(clip.id);
                        const isClipDisabled =
                          publishRangeLocked ||
                          isExistingClipRecord ||
                          isClipPublished;
                        const clipDuration =
                          clip.duration ||
                          Math.max(
                            0,
                            (clip.end_time || 0) - (clip.start_time || 0)
                          );
                        const clipThumb =
                          clip.thumb && clip.base
                            ? `${clip.base}/${clip.thumb}_180.jpg`
                            : "";
                        return (
                          <button
                            key={clip.id}
                            type="button"
                            disabled={isClipDisabled}
                            title={
                              isExistingClipRecord
                                ? isSelected
                                  ? "This record was published from this clip."
                                  : "Editing an existing clip publish - switch clips isn't supported here."
                                : isClipPublished
                                  ? "Already published to this channel."
                                  : undefined
                            }
                            onClick={() => toggleClip(clip.id)}
                            className="d-flex align-items-center gap-2"
                            style={{
                              border: isSelected
                                ? "1px solid #0d6efd"
                                : "1px solid #dee2e6",
                              background: "#fff",
                              borderRadius: "6px",
                              padding: "2px 10px 2px 2px",
                              cursor: isClipDisabled
                                ? "not-allowed"
                                : "pointer",
                              opacity: isClipPublished ? 0.5 : 1,
                              width: "220px",
                            }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{
                                width: "75px",
                                aspectRatio: "16 / 9",
                                borderRadius: "4px",
                                background: clipThumb
                                  ? `#161327 url(${clipThumb}) center/cover no-repeat`
                                  : "#161327",
                                color: "#fff",
                                position: "relative",
                                flexShrink: 0,
                              }}
                            >
                              {!clipThumb && (
                                <i
                                  className="bi bi-play-circle-fill"
                                  style={{ fontSize: "14px" }}
                                />
                              )}
                              {isSelected && (
                                <i
                                  className="bi bi-check-circle-fill"
                                  style={{
                                    position: "absolute",
                                    top: "-6px",
                                    left: "-6px",
                                    background: "#fff",
                                    borderRadius: "50%",
                                    fontSize: "13px",
                                    color: "#0d6efd",
                                  }}
                                />
                              )}
                              <span
                                style={{
                                  position: "absolute",
                                  bottom: "2px",
                                  right: "2px",
                                  background: "rgba(0, 0, 0, 0.8)",
                                  color: "#fff",
                                  fontSize: "10px",
                                  lineHeight: 1,
                                  padding: "2px 4px",
                                  borderRadius: "3px",
                                }}
                              >
                                {isClipPublished
                                  ? "Published"
                                  : secondsToHms(clipDuration)}
                              </span>
                            </div>
                            <div
                              className="d-flex flex-column align-items-start"
                              style={{ minWidth: 0, flex: 1 }}
                            >
                              <span
                                className="text-truncate w-100 f-12 fw-medium text-start"
                                style={{}}
                              >
                                {clip.title || "Untitled clip"}
                              </span>
                              {clip.description && (
                                <span
                                  className="text-truncate w-100 text-start"
                                  style={{ fontSize: "11px", color: "#868e96" }}
                                >
                                  {clip.description}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          {(videoRangeMode === PUBLISH_RANGE_TYPE.FULL ||
            videoRangeMode === PUBLISH_RANGE_TYPE.SEGMENT ||
            (videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS &&
              !activeClipTabId)) && (
            <>
              {/* Full-video meta fields — reused as-is for Full video and Custom
              segment. In Clips mode (before a clip is selected) they're shown
              read-only, seeded with the current full-video values, so the
              user sees what would publish before choosing a clip to edit. */}
              {(function renderFullVideoFields(
                disabled: boolean,
                dimmed: boolean
              ) {
                return (
                  <div className="col-md-12">
                    <div
                      className="row gy-3 gx-5"
                      style={{
                        opacity: dimmed ? 0.5 : 1,
                        pointerEvents: dimmed ? "none" : "auto",
                        width: "100%",
                        marginTop: "10px",
                      }}
                    >
                      {/* Title Field */}
                      <div className="col-md-12">
                        {customPrompt.title.open && !isPreset && (
                          <TextArea
                            label="Custom Prompt"
                            name="customPromptTitle"
                            showRequired={false}
                            placeholder="Enter your custom prompt..."
                            value={customPrompt.title.text}
                            onBlur={formik.handleBlur}
                            onChange={(e) =>
                              handleCustomPromptTextChange(
                                "title",
                                e.target.value
                              )
                            }
                            error={
                              customPrompt.title.text.length > 500
                                ? "Prompt should be less than 500 characters"
                                : undefined
                            }
                            touched={customPrompt.title.text.length > 500}
                            characterLimit={500}
                            onSend={() => handlePromptSend("title")}
                            isSending={customPrompt.title.isSending}
                            style={{
                              marginBottom: "1rem",
                              backgroundColor: "#fff",
                            }}
                          />
                        )}
                        {!isPreset && (
                          <TextInput
                            label={config.titleLabel}
                            name="title"
                            value={formik.values?.title ?? ""}
                            characterLimit={config.titleCharacterLimit}
                            currentCount={countGraphemes(
                              formik.values?.title ?? ""
                            )}
                            placeholder={config.titlePlaceholder}
                            showRequired={true}
                            onChange={(e) => {
                              markFieldEdited("title");
                              formik.handleChange(e);
                              formik.setFieldTouched("title", true, false);
                              formik.validateField("title");
                            }}
                            onBlur={handleTitleBlur}
                            error={formik.errors.title}
                            touched={formik.touched.title}
                            disabled={disabled}
                            rightElement={
                              <div className="d-flex align-items-center gap-2">
                                {platformType === "twitter" && (
                                  <span className="text-muted small">
                                    ({visibleCharCount}/
                                    {config.titleCharacterLimit})
                                  </span>
                                )}
                                {isTitleBlurLoading ? (
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="spinner-border spinner-border-sm text-primary"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <AIAssistButtons
                                    field="title"
                                    isOpen={activeField === "title"}
                                    historyFromProps={titleHistory}
                                    currentHistoryIndex={
                                      currentTitleHistoryIndex
                                    }
                                    onHistoryNavigation={
                                      handleTitleHistoryNavigation
                                    }
                                    onAddToHistory={(text: string) =>
                                      addToHistory(
                                        text,
                                        setTitleHistory,
                                        setCurrentTitleHistoryIndex
                                      )
                                    }
                                    setHistory={setTitleHistory}
                                    setIndex={setCurrentTitleHistoryIndex}
                                    formik={formik}
                                    setisDescriptionAiButtonLoading={
                                      setIsTitleAiButtonLoading
                                    }
                                    onToggle={() =>
                                      setActiveField(
                                        activeField === "title" ? null : "title"
                                      )
                                    }
                                    textToProcess={formik.values.title}
                                    mediaId={initialdata.media_id}
                                    onResponseReceived={(response) => {
                                      markFieldEdited("title");
                                      formik.setFieldValue("title", response);
                                      addToHistory(
                                        response,
                                        setTitleHistory,
                                        setCurrentTitleHistoryIndex
                                      );
                                    }}
                                    position="right"
                                    onCustomPromptClick={() =>
                                      handleCustomPromptClick("title")
                                    }
                                    customPromptOpen={customPrompt.title.open}
                                    onCustomPromptClose={() =>
                                      handleCustomPromptClose("title")
                                    }
                                  />
                                )}
                              </div>
                            }
                          />
                        )}
                      </div>
                      {/* Description Field */}
                      {config.showDescription && (
                        <div className="col-md-12">
                          {customPrompt.description.open && !isPreset && (
                            <TextArea
                              label="Custom Prompt"
                              name="customPromptDesc"
                              showRequired={false}
                              placeholder="Enter your custom prompt..."
                              value={customPrompt.description.text}
                              onBlur={formik.handleBlur}
                              onChange={(e) =>
                                handleCustomPromptTextChange(
                                  "description",
                                  e.target.value
                                )
                              }
                              error={
                                customPrompt.description.text.length > 500
                                  ? "Prompt should be less than 500 characters"
                                  : undefined
                              }
                              touched={
                                customPrompt.description.text.length > 500
                              }
                              characterLimit={500}
                              onSend={() => handlePromptSend("description")}
                              isSending={customPrompt.description.isSending}
                              style={{
                                marginBottom: "1rem",
                                backgroundColor: "#fff",
                              }}
                            />
                          )}
                          {!isPreset && (
                            <TextArea
                              label="DESCRIPTION"
                              name="desc"
                              value={formik.values?.desc ?? ""}
                              characterLimit={config.descriptionCharacterLimit}
                              placeholder="Enter description for publish"
                              showRequired={true}
                              onChange={(e) => {
                                markFieldEdited("desc");
                                formik.handleChange(e);
                                formik.setFieldTouched("desc", true, false);
                                formik.validateField("desc");
                              }}
                              onBlur={handleDescriptionBlur}
                              style={{
                                height: "100px",
                                resize: "none",
                                lineHeight: "20px",
                              }}
                              touched={formik.touched.desc}
                              error={formik.errors.desc}
                              disabled={disabled}
                              rightElement={
                                <div className="d-flex align-items-center gap-2">
                                  {isDescriptionBlurLoading ? (
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="spinner-border spinner-border-sm text-primary"
                                        role="status"
                                      >
                                        <span className="visually-hidden">
                                          Loading...
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <AIAssistButtons
                                      field="description"
                                      isOpen={activeField === "description"}
                                      historyFromProps={descriptionHistory}
                                      currentHistoryIndex={
                                        currentDescriptionHistoryIndex
                                      }
                                      onHistoryNavigation={
                                        handleDescriptionHistoryNavigation
                                      }
                                      onAddToHistory={(text: string) =>
                                        addToHistory(
                                          text,
                                          setDescriptionHistory,
                                          setCurrentDescriptionHistoryIndex
                                        )
                                      }
                                      setHistory={setDescriptionHistory}
                                      setIndex={
                                        setCurrentDescriptionHistoryIndex
                                      }
                                      formik={formik}
                                      setisDescriptionAiButtonLoading={
                                        setIsDescriptionAiButtonLoading
                                      }
                                      onToggle={() =>
                                        setActiveField(
                                          activeField === "description"
                                            ? null
                                            : "description"
                                        )
                                      }
                                      textToProcess={formik.values.desc}
                                      mediaId={initialdata.media_id}
                                      onResponseReceived={(response) => {
                                        markFieldEdited("desc");
                                        formik.setFieldValue("desc", response);
                                        addToHistory(
                                          response,
                                          setDescriptionHistory,
                                          setCurrentDescriptionHistoryIndex
                                        );
                                      }}
                                      position="right"
                                      onCustomPromptClick={() =>
                                        handleCustomPromptClick("description")
                                      }
                                      customPromptOpen={
                                        customPrompt.description.open
                                      }
                                      onCustomPromptClose={() =>
                                        handleCustomPromptClose("description")
                                      }
                                    />
                                  )}
                                </div>
                              }
                            />
                          )}
                        </div>
                      )}
                      {/* Preset Mode Title/Description */}
                      {isPreset && (
                        <>
                          <div className="input-wrapper">
                            <label className="checkbox-label">
                              {config.titleLabel}
                              {!isPreview && (
                                <span className="text-danger">*</span>
                              )}
                            </label>
                            <TextInput
                              name="title"
                              placeholder="Enter title here..."
                              value={formik.values.title}
                              onChange={(e) => {
                                formik.handleChange(e);
                                formik.setFieldTouched("title", true, false);
                                formik.validateField("title");
                              }}
                              onBlur={formik.handleBlur}
                              showRequired={false}
                              error={formik.errors.title as string}
                              touched={formik.touched.title}
                              disabled={isPreview}
                            />
                            <AiTransformer
                              type={formik.values.title_ai_type}
                              defaultValue={formik.values.title_ai_value}
                              onSelect={handleTitleAiTypeChange}
                              disabled={
                                isPreview || !enabledFields.title_ai_checked
                              }
                              item={item}
                              isChecked={enabledFields.title_ai_checked}
                              readOnlyMode={readOnlyMode}
                              handleCheckboxChange={(value: any) => {
                                handleFieldToggle("title_ai_checked", value);
                                formik.setFieldValue("title_ai_checked", value);
                              }}
                            />
                          </div>
                          {config.showDescription && (
                            <div className="input-wrapper">
                              <label className="checkbox-label">
                                DESCRIPTION
                                {!isPreview && (
                                  <span className="text-danger">*</span>
                                )}
                              </label>
                              <TextInput
                                name="desc"
                                placeholder="Enter description here..."
                                value={formik.values.desc}
                                onChange={(e) => {
                                  formik.handleChange(e);
                                  formik.setFieldTouched("desc", true, false);
                                  formik.validateField("desc");
                                }}
                                onBlur={formik.handleBlur}
                                showRequired={false}
                                error={formik.errors.desc as string}
                                touched={formik.touched.desc}
                                disabled={isPreview}
                              />
                              <AiTransformer
                                type={formik.values.desc_ai_type}
                                defaultValue={formik.values.desc_ai_value}
                                onSelect={handleDescAiTypeChange}
                                item={item}
                                disabled={
                                  isPreview || !enabledFields.desc_ai_checked
                                }
                                isChecked={enabledFields.desc_ai_checked}
                                readOnlyMode={readOnlyMode}
                                handleCheckboxChange={(value: any) => {
                                  handleFieldToggle("desc_ai_checked", value);
                                  formik.setFieldValue(
                                    "desc_ai_checked",
                                    value
                                  );
                                }}
                              />
                            </div>
                          )}
                        </>
                      )}
                      {/* Tags */}
                      {config.showTags && (
                        <div className="col-md-12">
                          <ChipInput
                            label="TAGS"
                            value={formik.values.tags}
                            handleChipChange={(
                              chip: string | string[],
                              action: string
                            ) => {
                              const currentTags = formik.values.tags || [];
                              let nextTags: string[];
                              if (action === "add") {
                                const chipsToAdd =
                                  typeof chip === "string" ? [chip] : chip;
                                nextTags = [
                                  ...new Set([...currentTags, ...chipsToAdd]),
                                ];
                              } else if (action === "remove") {
                                const chipsToRemove =
                                  typeof chip === "string" ? [chip] : chip;
                                nextTags = currentTags.filter(
                                  (t: string) => !chipsToRemove.includes(t)
                                );
                              } else if (action === "clear") {
                                nextTags = [];
                              } else {
                                nextTags = currentTags;
                              }
                              markFieldEdited("tags");
                              formik.setFieldTouched("tags", true, false);
                              shouldValidateTagsRef.current = true;
                              void formik
                                .setFieldValue("tags", nextTags)
                                .then(() => {
                                  void formik.validateField("tags");
                                });
                            }}
                            showRequired={!isPreview}
                            touched={formik.touched.tags}
                            error={
                              formik.errors.tags &&
                              typeof formik.errors.tags === "string"
                                ? formik.errors.tags
                                : undefined
                            }
                            onBlur={() => {
                              formik.setFieldTouched("tags", true);
                              shouldValidateTagsRef.current = true;
                            }}
                            disabled={disabled || isPreview}
                            labelCheckbox={false}
                            labelCheckboxChecked={enabledFields.tags}
                            onLabelCheckboxChange={(newValue: any) => {
                              handleFieldToggle("tags", newValue);
                            }}
                            disableLabelCheckbox={
                              isPreview && !(formik.values.tags.length > 0)
                            }
                            readOnlyMode={readOnlyMode}
                            isYoutube={platformType === "youtube"}
                          />
                        </div>
                      )}
                      {/* Platform fields row */}
                      {/* Preset/preview path keeps legacy platform-specific fields callback */}

                      {renderPlatformSpecificFields &&
                        renderPlatformSpecificFields(
                          formik,
                          config,
                          enabledFields,
                          handleFieldToggle,
                          monetizationOptions,
                          youtubePlaylist,
                          isSftp,
                          // Category/Playlist: normal disabled, unlocks once
                          // Edit -> Sync -> Update reaches "update".
                          disabled,
                          // Rights management / created-for-kids / monetize
                          // are never editable on an already-published video,
                          // even once the above unlocks - reuse the same lock
                          // the slate edit/delete buttons already apply for
                          // the same reason.
                          lockedOnPublished
                        )}
                      <div
                        className={
                          platformType === "youtube"
                            ? "col-md-3"
                            : "col col-md-5"
                        }
                      >
                        <CustomSelect
                          value={formik.values.privacy ?? config.defaultPrivacy}
                          label="VISIBILITY"
                          options={config.privacyOptions}
                          handleChange={(newValue: any) => {
                            formik.setFieldValue("privacy", newValue.value);
                            setShowScheduler(newValue.value === "schedule");
                            if (isPreset && newValue.value === "schedule") {
                              formik.setFieldValue("schedule_after", 15);
                            } else {
                              formik.setFieldValue("schedule_after", 0);
                            }
                            if (initialdata.schedule) {
                              setScheduled(
                                new Date(initialdata.schedule).getTime()
                              );
                            }
                          }}
                          disabled={
                            // LinkedIn and Facebook never allow changing
                            // visibility/privacy on an already-published
                            // video, even mid Edit -> Sync -> Update - same
                            // lock as YouTube's rights management/created-
                            // for-kids/monetize fields above.
                            (platformType === "linkedin" ||
                            platformType === "facebook"
                              ? lockedOnPublished
                              : disabled) || isPreview
                          }
                          showRequired={!isPreview}
                          labelCheckbox={false}
                          labelCheckboxChecked={enabledFields.privacy}
                          onLabelCheckboxChange={(newValue: any) => {
                            handleFieldToggle("privacy", newValue);
                          }}
                          disableLabelCheckbox={
                            isPreview && !(formik.values.privacy.length > 0)
                          }
                          readOnlyMode={readOnlyMode}
                          touched={formik.touched.privacy}
                          error={formik.errors.privacy}
                        />
                      </div>
                      {/* Scheduler */}
                      {showScheduler && !isPreset && config.showScheduler && (
                        <div
                          className={
                            platformType === "youtube"
                              ? "col-md-6 mt-4"
                              : "col col-md-6 mt-4"
                          }
                        >
                          <UnifiedDateTimePicker
                            mode="datetime"
                            disabled={formik.values.isNew !== 100}
                            onChange={handleDateTimeSelected}
                            value={scheduled || undefined}
                            outputFormat="timestamp"
                            timeAdded={
                              platformType === "youtube" ? 15 : undefined
                            }
                          />
                        </div>
                      )}
                      {showScheduler &&
                        isPreset &&
                        config.showScheduleAfter && (
                          <div
                            className={
                              platformType === "youtube"
                                ? "col-md-6 mt-4"
                                : "col col-md-6 mt-4"
                            }
                          >
                            <CustomSelect
                              value={formik.values.schedule_after ?? 0}
                              label="Schedule After"
                              options={[
                                { label: "15 Minutes", value: 15 },
                                { label: "30 Minutes", value: 30 },
                                { label: "45 Minutes", value: 45 },
                                { label: "1 Hour", value: 60 },
                              ]}
                              handleChange={(newValue: any) => {
                                formik.setFieldValue(
                                  "schedule_after",
                                  newValue.value
                                );
                              }}
                              disabled={disabled || isPreview}
                              showRequired={false}
                            />
                          </div>
                        )}
                      {/* Thumbnail (below fields) */}
                      {!isPreset && config.showThumbnail && (
                        <div className="col-md-6 position-relative">
                          <label className="form-label">
                            THUMBNAIL<span className="required ms-1">*</span>
                          </label>
                          <ThumbnailSelection
                            baseUrl={
                              assets?.length > 0
                                ? assets[0]?.baseurl || ""
                                : mediaBaseUrl
                            }
                            assets={assets}
                            handleThumbnailChange={(
                              url: string,
                              id: string
                            ) => {
                              markFieldEdited("thumbnail");
                              handleThumbnailChange(url, id);
                            }}
                            currentThumbnail={formik.values.thumb}
                            currentThumbnailId={formik.values.thumbId}
                            disabled={disabled}
                            error={
                              thumbnailDimensionError || formik.errors.thumbId
                            }
                            touched={
                              !!thumbnailDimensionError ||
                              formik.touched.thumbId
                            }
                            updateModal={updateModal}
                            isWizard={isWizard}
                          />
                        </div>
                      )}
                      {/* Custom Meta Rules - YouTube only */}
                      {platformType === "youtube" &&
                        socialData?.conditional_rules &&
                        socialData?.conditional_rules?.length > 0 && (
                          <div className="col col-md-6">
                            <CustomMetaRules
                              disabled={disabled}
                              isPreview={isPreview}
                              readOnlyMode={true}
                              youtubePlaylist={youtubePlaylist}
                              monetizationOptions={monetizationOptions}
                              bunchLanguageList={bunchLanguageList}
                              contentType={[]}
                              formik={formik}
                              initialRules={
                                initialdata?.conditional_rules || []
                              }
                              onRulesChange={(rules: ConditionalRules[]) => {
                                formik.setFieldValue(
                                  "conditional_rules",
                                  rules
                                );
                              }}
                            />
                          </div>
                        )}
                      {/* Slates */}
                      {!isPreset &&
                        !isPreview &&
                        !!currentMediaDetails?.orientation &&
                        currentMediaDetails.orientation !== 0 &&
                        (() => {
                          const channelHasSlate =
                            !!channelPreSlate || !!channelPostSlate;
                          const hasExistingSlate =
                            !!formik.values.preVideo ||
                            !!formik.values.postVideo;
                          const showPickers =
                            channelHasSlate ||
                            slateBandExpanded ||
                            hasExistingSlate;

                          if (!showPickers) {
                            return (
                              <div className="col-md-6">
                                <label
                                  className="form-label"
                                  style={{ marginBottom: "32px" }}
                                >
                                  SLATES
                                </label>
                                <div className="d-flex align-items-center px-1 py-1">
                                  {!lockedOnPublished ? (
                                    <button
                                      type="button"
                                      aria-label="Add pre / post slate"
                                      onClick={() => setSlateBandExpanded(true)}
                                      style={{
                                        border: "none",
                                        background: "transparent",
                                        color: "#0d6efd",
                                        fontSize: "12px",
                                        cursor: "pointer",
                                        padding: 0,
                                        textDecoration: "underline", // persistent affordance — reads as an action
                                      }}
                                    >
                                      + Add
                                    </button>
                                  ) : (
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#868e96",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      not set
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div className="col-md-6">
                              <label
                                className="form-label"
                                style={{ marginBottom: "32px" }}
                              >
                                SLATES
                              </label>
                              <div className="d-flex flex-wrap gap-3">
                                <div style={{ flex: "1 1 240px" }}>
                                  {selectedPreSlate && !isPreSlateEdit ? (
                                    <div style={{ position: "relative" }}>
                                      <SelectedMediaCard
                                        media={selectedPreSlate}
                                        type="pre"
                                        onClose={() => handleSlateClose("pre")}
                                        onEdit={() => handleSlateEdit("pre")}
                                        showEditButton={!lockedOnPublished}
                                        showDeleteButton={!lockedOnPublished}
                                        showPreviewButton={true}
                                        cardStyle={{
                                          width: "60px",
                                          minWidth: "60px",
                                        }}
                                        style={{ width: "100%" }}
                                        maxLength={20}
                                      />
                                      <span
                                        className="badge bg-secondary"
                                        style={{
                                          position: "absolute",
                                          bottom: "4px",
                                          right: "4px",
                                          fontSize: "9px",
                                          fontWeight: 500,
                                          letterSpacing: "0.03em",
                                          zIndex: 1,
                                        }}
                                      >
                                        OPENING
                                      </span>
                                    </div>
                                  ) : (
                                    <InputWithMediaInfo
                                      label="OPENING SLATE"
                                      ref={preSlateInputRef}
                                      value={formik.values.preVideo}
                                      handleChange={(newValue: any) => {
                                        formik.setFieldValue(
                                          "preVideo",
                                          newValue.label
                                        );
                                        setSelectedPreSlate(newValue);
                                        setIsPreSlateEdit(false);
                                      }}
                                      options={preMediaList}
                                      onInputChangeCallback={(params: any) => {
                                        setPreSlateInputText(params.keyword);
                                        dispatch(
                                          getSearchedListMedia({
                                            stitle: params.keyword,
                                            asset_type: "slates",
                                            key: "pre",
                                            ...(currentMediaDetails?.orientation
                                              ? {
                                                  orientation:
                                                    currentMediaDetails.orientation,
                                                }
                                              : {}),
                                          } as any) as any
                                        );
                                      }}
                                      handleBlur={() =>
                                        setIsPreSlateEdit(false)
                                      }
                                      hideArrow={true}
                                      right="11px"
                                      initialStructuredData={selectedPreSlate}
                                      initialInputText={
                                        isPreSlateEdit && selectedPreSlate
                                          ? String(selectedPreSlate.value ?? "")
                                          : preSlateInputText || undefined
                                      }
                                      loader={true}
                                      loaderRight="11px"
                                      disabled={lockedOnPublished}
                                    />
                                  )}
                                </div>
                                <div style={{ flex: "1 1 240px" }}>
                                  {selectedPostSlate && !isPostSlateEdit ? (
                                    <div style={{ position: "relative" }}>
                                      <SelectedMediaCard
                                        media={selectedPostSlate}
                                        type="post"
                                        onClose={() => handleSlateClose("post")}
                                        onEdit={() => handleSlateEdit("post")}
                                        showEditButton={!lockedOnPublished}
                                        showDeleteButton={!lockedOnPublished}
                                        showPreviewButton={true}
                                        cardStyle={{
                                          width: "60px",
                                          minWidth: "60px",
                                        }}
                                        style={{ width: "100%" }}
                                        maxLength={20}
                                      />
                                      <span
                                        className="badge bg-secondary"
                                        style={{
                                          position: "absolute",
                                          bottom: "4px",
                                          right: "4px",
                                          fontSize: "9px",
                                          fontWeight: 500,
                                          letterSpacing: "0.03em",
                                          zIndex: 1,
                                        }}
                                      >
                                        CLOSING
                                      </span>
                                    </div>
                                  ) : (
                                    <InputWithMediaInfo
                                      label="CLOSING SLATE"
                                      ref={postSlateInputRef}
                                      value={formik.values.postVideo}
                                      handleChange={(newValue: any) => {
                                        formik.setFieldValue(
                                          "postVideo",
                                          newValue.label
                                        );
                                        setSelectedPostSlate(newValue);
                                        setIsPostSlateEdit(false);
                                      }}
                                      options={postMediaList}
                                      onInputChangeCallback={(params: any) => {
                                        setPostSlateInputText(params.keyword);
                                        dispatch(
                                          getSearchedListMedia({
                                            stitle: params.keyword,
                                            asset_type: "slates",
                                            key: "post",
                                            ...(currentMediaDetails?.orientation
                                              ? {
                                                  orientation:
                                                    currentMediaDetails.orientation,
                                                }
                                              : {}),
                                          } as any) as any
                                        );
                                      }}
                                      handleBlur={() =>
                                        setIsPostSlateEdit(false)
                                      }
                                      hideArrow={true}
                                      right="11px"
                                      initialStructuredData={selectedPostSlate}
                                      initialInputText={
                                        isPostSlateEdit && selectedPostSlate
                                          ? String(
                                              selectedPostSlate.value ?? ""
                                            )
                                          : postSlateInputText || undefined
                                      }
                                      loader={true}
                                      loaderRight="11px"
                                      disabled={lockedOnPublished}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      {/* <div className="col col-md-8 border">

            </div> */}
                    </div>
                  </div>
                );
              })(
                videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS ? true : disabled,
                videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS && !activeClipTabId
              )}
            </>
          )}
          {videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS &&
            selectedClipIds.length > 0 &&
            (() => {
              const active = activeClipTabId
                ? clipFieldValues[activeClipTabId]
                : undefined;
              const updateActive = (patch: Partial<ClipFieldValues>) => {
                if (!activeClipTabId) return;
                setClipFieldValues((cv) => ({
                  ...cv,
                  [activeClipTabId]: { ...cv[activeClipTabId], ...patch },
                }));
              };
              return (
                <div className="col col-md-12">
                  <>
                    <div
                      className="d-flex flex-wrap gap-2 my-3 border-bottom"
                      style={{ borderColor: "#dee2e6" }}
                    >
                      {selectedClipIds.map((id) => {
                        const clip = availableClips.find((c) => c.id === id);
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setActiveClipTabId(id)}
                            style={{
                              border: "none",
                              borderBottom:
                                activeClipTabId === id
                                  ? "2px solid #0d6efd"
                                  : "2px solid transparent",
                              background: "transparent",
                              color:
                                activeClipTabId === id ? "#0d6efd" : "#495057",
                              fontWeight: activeClipTabId === id ? 600 : 400,
                              padding: "4px 10px",
                              fontSize: "13px",
                            }}
                          >
                            {clip?.title || id}
                          </button>
                        );
                      })}
                    </div>
                    {active && activeClipTabId && (
                      <div className="d-flex flex-column gap-3">
                        <TextInput
                          label="Title"
                          name={`clip-title-${activeClipTabId}`}
                          value={active.title}
                          showRequired
                          placeholder="Enter clip title"
                          onChange={(e: any) =>
                            updateActive({ title: e.target.value })
                          }
                          onBlur={() => {}}
                          error={
                            !active.title?.trim()
                              ? "Title is required"
                              : undefined
                          }
                          touched={clipFieldsTouched}
                          disabled={disabled}
                        />
                        <TextArea
                          label="Description"
                          name={`clip-desc-${activeClipTabId}`}
                          showRequired
                          placeholder="Enter clip description"
                          value={active.desc}
                          onChange={(e: any) =>
                            updateActive({ desc: e.target.value })
                          }
                          onBlur={() => {}}
                          error={
                            !active.desc?.trim()
                              ? "Description is required"
                              : undefined
                          }
                          touched={clipFieldsTouched}
                          disabled={disabled}
                        />
                        <ChipInput
                          label="Tags"
                          value={active.tags}
                          showRequired
                          // ChipInput shows `error` unconditionally (unlike
                          // TextInput/TextArea/CustomSelect, which gate on
                          // `touched` themselves) - compute it as
                          // touched-gated here instead of relying on the
                          // `touched` prop below to suppress it.
                          error={
                            clipFieldsTouched && !active.tags?.length
                              ? "At least one tag is required"
                              : undefined
                          }
                          touched={clipFieldsTouched}
                          handleChipChange={(chip, action) => {
                            // ChipInput always calls back with an array (even
                            // for a single chip, or several from pasted
                            // comma-separated text) on "add"/"remove", never a
                            // bare string - treating a single string as the
                            // whole new list (as this used to) silently
                            // dropped every other existing tag.
                            const currentTags = active.tags || [];
                            if (action === "add") {
                              const chipsToAdd =
                                typeof chip === "string" ? [chip] : chip;
                              updateActive({
                                tags: [
                                  ...new Set([...currentTags, ...chipsToAdd]),
                                ],
                              });
                            } else if (action === "remove") {
                              const chipsToRemove =
                                typeof chip === "string" ? [chip] : chip;
                              updateActive({
                                tags: currentTags.filter(
                                  (t) => !chipsToRemove.includes(t)
                                ),
                              });
                            } else if (action === "clear") {
                              updateActive({ tags: [] });
                            }
                          }}
                          disabled={disabled}
                        />
                        {platformType === "youtube" && (
                          <div className="d-flex flex-wrap gap-3">
                            <div style={{ flex: "1 1 200px" }}>
                              <CustomSelect
                                label="CATEGORY"
                                value={active.category}
                                options={Object.entries(
                                  DEFAULT_YOUTUBE_CATEGORIES
                                ).map(([key, value]) => ({
                                  label: value as string,
                                  value: key,
                                }))}
                                handleChange={(newValue: any) =>
                                  updateActive({ category: newValue.value })
                                }
                                disabled={disabled}
                                showRequired
                                error={
                                  !active.category
                                    ? "Category is required"
                                    : undefined
                                }
                                touched={clipFieldsTouched}
                              />
                            </div>
                            {isSftp && (
                              <div style={{ flex: "1 1 200px" }}>
                                <CustomSelect
                                  label="MONETIZE VIDEO"
                                  value={active.monetize_policy_id || ""}
                                  options={
                                    monetizationOptions?.map((option) => ({
                                      label: option.title,
                                      value: option.id,
                                    })) || []
                                  }
                                  handleChange={(newValue: any) =>
                                    updateActive({
                                      monetize_policy_id: newValue.value,
                                      monetize_policy_name: newValue.label,
                                    })
                                  }
                                  disabled={lockedOnPublished}
                                  showRequired
                                  error={
                                    !active.monetize_policy_id
                                      ? "Monetization is required"
                                      : undefined
                                  }
                                  touched={clipFieldsTouched}
                                />
                              </div>
                            )}
                            <div style={{ flex: "1 1 200px" }}>
                              <CustomSelect
                                label="ADD TO PLAYLIST"
                                value={
                                  active.playlist
                                    ? active.playlist.split(",").filter(Boolean)
                                    : []
                                }
                                options={(youtubePlaylist || []).map((p) => ({
                                  label: p.title,
                                  value: p.id,
                                }))}
                                multiSelect={true}
                                handleChange={(newValue: any) =>
                                  updateActive({
                                    playlist: (newValue || [])
                                      .map((v: any) => v.value)
                                      .join(","),
                                  })
                                }
                                disabled={disabled}
                                showRequired={false}
                              />
                            </div>
                            <div style={{ flex: "1 1 200px" }}>
                              <CustomSelect
                                label="CREATED FOR KIDS"
                                value={active.is_created_for_kids}
                                options={[
                                  { label: "Yes", value: "yes" },
                                  { label: "No", value: "no" },
                                ]}
                                handleChange={(newValue: any) =>
                                  updateActive({
                                    is_created_for_kids: newValue.value,
                                  })
                                }
                                disabled={lockedOnPublished}
                                showRequired
                                error={
                                  !active.is_created_for_kids
                                    ? "This field is required"
                                    : undefined
                                }
                                touched={clipFieldsTouched}
                              />
                            </div>
                            {isSftp && (
                              <div style={{ flex: "1 1 200px" }}>
                                <CustomSelect
                                  label="RIGHTS MANAGEMENTS"
                                  value={
                                    active.is_rights_managements_allowed ??
                                    "yes"
                                  }
                                  options={[
                                    { label: "Yes", value: "yes" },
                                    { label: "No", value: "no" },
                                  ]}
                                  handleChange={(newValue: any) =>
                                    updateActive({
                                      is_rights_managements_allowed:
                                        newValue.value,
                                    })
                                  }
                                  disabled={lockedOnPublished}
                                  showRequired
                                  error={
                                    !active.is_rights_managements_allowed
                                      ? "This field is required"
                                      : undefined
                                  }
                                  touched={clipFieldsTouched}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        <div className="d-flex flex-wrap gap-3">
                          <div style={{ flex: "1 1 200px" }}>
                            <CustomSelect
                              label="VISIBILITY"
                              value={active.privacy}
                              options={[
                                { label: "Public", value: "public" },
                                { label: "Unlisted", value: "unlisted" },
                                { label: "Private", value: "private" },
                              ]}
                              handleChange={(newValue: any) =>
                                updateActive({ privacy: newValue.value })
                              }
                              disabled={
                                platformType === "linkedin" ||
                                platformType === "facebook"
                                  ? lockedOnPublished
                                  : disabled
                              }
                              showRequired
                              error={
                                !active.privacy
                                  ? "Visibility is required"
                                  : undefined
                              }
                              touched={clipFieldsTouched}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="form-label">
                            THUMBNAIL <span className="text-danger">*</span>
                          </label>
                          <ThumbnailSelection
                            baseUrl={
                              assets?.length > 0
                                ? assets[0]?.baseurl || ""
                                : mediaBaseUrl
                            }
                            assets={assets}
                            handleThumbnailChange={(url: string, id: string) =>
                              updateActive({ thumb: url, thumbId: id })
                            }
                            currentThumbnail={active.thumb}
                            currentThumbnailId={active.thumbId}
                            disabled={disabled}
                            updateModal={updateModal}
                            isWizard={isWizard}
                          />
                          {clipFieldsTouched && !active.thumbId && (
                            <div
                              className="text-danger mt-1"
                              style={{ fontSize: "11px" }}
                            >
                              Thumbnail is required
                            </div>
                          )}
                        </div>
                        {/* Slates - a single destination-level setting (not
                        per-clip: every selected clip's payload gets the same
                        resolvedPre/resolvedPost in the fanout above), so it
                        must still show once a clip is active, not only in
                        renderFullVideoFields (which stops rendering as soon
                        as activeClipTabId is set). */}
                        {!isPreset &&
                          !isPreview &&
                          !!currentMediaDetails?.orientation &&
                          currentMediaDetails.orientation !== 0 &&
                          (() => {
                            const channelHasSlate =
                              !!channelPreSlate || !!channelPostSlate;
                            const hasExistingSlate =
                              !!formik.values.preVideo ||
                              !!formik.values.postVideo;
                            const showPickers =
                              channelHasSlate ||
                              slateBandExpanded ||
                              hasExistingSlate;

                            if (!showPickers) {
                              return (
                                <div>
                                  <label
                                    className="form-label"
                                    style={{ marginBottom: "8px" }}
                                  >
                                    SLATES
                                  </label>
                                  <div className="d-flex align-items-center px-1 py-1">
                                    {!lockedOnPublished ? (
                                      <button
                                        type="button"
                                        aria-label="Add pre / post slate"
                                        onClick={() =>
                                          setSlateBandExpanded(true)
                                        }
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          color: "#0d6efd",
                                          fontSize: "12px",
                                          cursor: "pointer",
                                          padding: 0,
                                          textDecoration: "underline",
                                        }}
                                      >
                                        + Add
                                      </button>
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#868e96",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        not set
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div>
                                <label
                                  className="form-label"
                                  style={{ marginBottom: "8px" }}
                                >
                                  SLATES
                                </label>
                                <div className="d-flex flex-wrap gap-3">
                                  <div style={{ flex: "1 1 240px" }}>
                                    {selectedPreSlate && !isPreSlateEdit ? (
                                      <div style={{ position: "relative" }}>
                                        <SelectedMediaCard
                                          media={selectedPreSlate}
                                          type="pre"
                                          onClose={() =>
                                            handleSlateClose("pre")
                                          }
                                          onEdit={() => handleSlateEdit("pre")}
                                          showEditButton={!lockedOnPublished}
                                          showDeleteButton={!lockedOnPublished}
                                          showPreviewButton={true}
                                          cardStyle={{
                                            width: "60px",
                                            minWidth: "60px",
                                          }}
                                          style={{ width: "100%" }}
                                          maxLength={20}
                                        />
                                        <span
                                          className="badge bg-secondary"
                                          style={{
                                            position: "absolute",
                                            bottom: "4px",
                                            right: "4px",
                                            fontSize: "9px",
                                            fontWeight: 500,
                                            letterSpacing: "0.03em",
                                            zIndex: 1,
                                          }}
                                        >
                                          OPENING
                                        </span>
                                      </div>
                                    ) : (
                                      <InputWithMediaInfo
                                        label="OPENING SLATE"
                                        ref={preSlateInputRef}
                                        value={formik.values.preVideo}
                                        handleChange={(newValue: any) => {
                                          formik.setFieldValue(
                                            "preVideo",
                                            newValue.label
                                          );
                                          setSelectedPreSlate(newValue);
                                          setIsPreSlateEdit(false);
                                        }}
                                        options={preMediaList}
                                        onInputChangeCallback={(
                                          params: any
                                        ) => {
                                          setPreSlateInputText(params.keyword);
                                          dispatch(
                                            getSearchedListMedia({
                                              stitle: params.keyword,
                                              asset_type: "slates",
                                              key: "pre",
                                              ...(currentMediaDetails?.orientation
                                                ? {
                                                    orientation:
                                                      currentMediaDetails.orientation,
                                                  }
                                                : {}),
                                            } as any) as any
                                          );
                                        }}
                                        handleBlur={() =>
                                          setIsPreSlateEdit(false)
                                        }
                                        hideArrow={true}
                                        right="11px"
                                        initialStructuredData={selectedPreSlate}
                                        initialInputText={
                                          isPreSlateEdit && selectedPreSlate
                                            ? String(
                                                selectedPreSlate.value ?? ""
                                              )
                                            : preSlateInputText || undefined
                                        }
                                        loader={true}
                                        loaderRight="11px"
                                        disabled={lockedOnPublished}
                                      />
                                    )}
                                  </div>
                                  <div style={{ flex: "1 1 240px" }}>
                                    {selectedPostSlate && !isPostSlateEdit ? (
                                      <div style={{ position: "relative" }}>
                                        <SelectedMediaCard
                                          media={selectedPostSlate}
                                          type="post"
                                          onClose={() =>
                                            handleSlateClose("post")
                                          }
                                          onEdit={() => handleSlateEdit("post")}
                                          showEditButton={!lockedOnPublished}
                                          showDeleteButton={!lockedOnPublished}
                                          showPreviewButton={true}
                                          cardStyle={{
                                            width: "60px",
                                            minWidth: "60px",
                                          }}
                                          style={{ width: "100%" }}
                                          maxLength={20}
                                        />
                                        <span
                                          className="badge bg-secondary"
                                          style={{
                                            position: "absolute",
                                            bottom: "4px",
                                            right: "4px",
                                            fontSize: "9px",
                                            fontWeight: 500,
                                            letterSpacing: "0.03em",
                                            zIndex: 1,
                                          }}
                                        >
                                          CLOSING
                                        </span>
                                      </div>
                                    ) : (
                                      <InputWithMediaInfo
                                        label="CLOSING SLATE"
                                        ref={postSlateInputRef}
                                        value={formik.values.postVideo}
                                        handleChange={(newValue: any) => {
                                          formik.setFieldValue(
                                            "postVideo",
                                            newValue.label
                                          );
                                          setSelectedPostSlate(newValue);
                                          setIsPostSlateEdit(false);
                                        }}
                                        options={postMediaList}
                                        onInputChangeCallback={(
                                          params: any
                                        ) => {
                                          setPostSlateInputText(params.keyword);
                                          dispatch(
                                            getSearchedListMedia({
                                              stitle: params.keyword,
                                              asset_type: "slates",
                                              key: "post",
                                              ...(currentMediaDetails?.orientation
                                                ? {
                                                    orientation:
                                                      currentMediaDetails.orientation,
                                                  }
                                                : {}),
                                            } as any) as any
                                          );
                                        }}
                                        handleBlur={() =>
                                          setIsPostSlateEdit(false)
                                        }
                                        hideArrow={true}
                                        right="11px"
                                        initialStructuredData={
                                          selectedPostSlate
                                        }
                                        initialInputText={
                                          isPostSlateEdit && selectedPostSlate
                                            ? String(
                                                selectedPostSlate.value ?? ""
                                              )
                                            : postSlateInputText || undefined
                                        }
                                        loader={true}
                                        loaderRight="11px"
                                        disabled={lockedOnPublished}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                      </div>
                    )}
                    <div
                      className="alert alert-info py-2 px-3 mt-3 mb-0"
                      style={{ fontSize: "11px", lineHeight: "1.3" }}
                    >
                      Publishing {selectedClipIds.length} clip
                      {selectedClipIds.length === 1 ? "" : "s"} will create a
                      separate post for each clip, one after another.
                    </div>
                  </>
                </div>
              );
            })()}
        </div>
      )}
      {/* Footer sits outside the scrollable fields row so, in the sheet, it stays
          pinned to the bottom (see .socialSheet-actions) instead of scrolling with them. */}
      {(inSheet || expandedId === initialdata.id) &&
        (() => {
          // A preview has nothing to cancel/delete/save/publish - it isn't
          // a real, editable publish entry.
          if (isPreview) return null;

          const effIsNew = formik.values.isNew ?? initialdata?.isNew;
          const effStatus = formik.values.status ?? initialdata?.status;
          const isEditableStatus =
            effStatus === 0 ||
            effStatus === 12 ||
            effStatus === -1 ||
            effStatus === -2;
          const actionsDisabled = effIsNew !== 100 && !isEditableStatus;
          // Clips mode with nothing picked yet has no valid payload to save/
          // publish/delete - block the footer actions until at least one clip
          // is selected.
          const noClipSelected =
            videoRangeMode === PUBLISH_RANGE_TYPE.CLIPS &&
            selectedClipIds.length === 0;
          const footerContent = (
            <>
              {/* Action Buttons */}
              <div
                className={`${
                  inSheet ? "" : "px-3 "
                }mt-3 d-flex justify-content-end border-top py-2 gap-2`}
              >
                <Button
                  type="danger"
                  HTMLButtonType="button"
                  className="me-auto"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (formik.values.isNew !== 100 && isPublishedMedia) {
                      setShowPublishedDeleteConfirm(true);
                      return;
                    }
                    const type =
                      formik.values.isNew !== 100 ? "delete" : "cancel";
                    if (type === "delete") {
                      setIsDeletingPublish(true);
                      try {
                        await handleCancelOrDelete(
                          initialdata,
                          type,
                          platformType
                        );
                      } finally {
                        setIsDeletingPublish(false);
                      }
                    } else {
                      handleCancelOrDelete(initialdata, type, platformType);
                    }
                  }}
                  isDisabled={
                    isDeletingPublish ||
                    noClipSelected ||
                    (formik.values.isNew !== 100 &&
                      !isPublishedMedia &&
                      !(
                        initialdata.status === -1 ||
                        initialdata.status === -2 ||
                        initialdata.status === 0 ||
                        initialdata.status === 12
                      ))
                  }
                >
                  {isDeletingPublish
                    ? "Resolving..."
                    : formik.values.isNew !== 100
                      ? "Delete"
                      : "Cancel"}
                </Button>

                {!isPreset &&
                  isPublishedMedia &&
                  publishEditStage === "update" &&
                  isVisibilityDowngradedSinceSync && (
                    <span className="text-danger small me-2 align-self-center">
                      *Changing visibility to {selectedVisibilityLabel} will
                      hide the video from public viewers.
                    </span>
                  )}

                {!isPreset &&
                  isPublishedMedia &&
                  publishEditStage === "update" &&
                  hasChangesSincePublishSync && (
                    // Reverts to the values captured right after sync — not the original
                    // pre-edit publish data, since sync may have pulled in fresh platform data.
                    <Button
                      type="text"
                      HTMLButtonType="button"
                      isDisabled={isUpdatingVideo}
                      onClick={(e) => {
                        e.preventDefault();
                        if (publishSyncSnapshotRef.current) {
                          formik.setValues(
                            JSON.parse(publishSyncSnapshotRef.current)
                          );
                        }
                        if (clipFieldValuesSyncSnapshotRef.current) {
                          setClipFieldValues(
                            JSON.parse(clipFieldValuesSyncSnapshotRef.current)
                          );
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  )}

                {!isPreset && isPublishedMedia && (
                  // Published media: Edit -> (auto) Sync Changes -> Update in place of Save/Publish.
                  // Sync/Update aren't gated by actionsDisabled — that flag exists for the
                  // draft/queue Save+Publish pair below and always trips true once published.
                  <Button
                    type={
                      publishEditStage === "update"
                        ? "outline-primary"
                        : "outline-primary"
                    }
                    HTMLButtonType="button"
                    isDisabled={
                      platformType === "instagram" ||
                      publishEditStage === "sync" ||
                      isUpdatingVideo ||
                      (publishEditStage === "update" &&
                        !hasChangesSincePublishSync)
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      handlePublishEditFlowClick();
                    }}
                  >
                    {publishEditStage === "sync" ? (
                      <span className="d-flex align-items-center gap-2">
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Syncing...</span>
                        </span>
                        Sync Changes
                      </span>
                    ) : isUpdatingVideo ? (
                      <span className="d-flex align-items-center gap-2">
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Updating...</span>
                        </span>
                        Updating...
                      </span>
                    ) : publishEditStage === "edit" ? (
                      "Edit"
                    ) : (
                      "Update"
                    )}
                  </Button>
                )}

                {!isPreset && !isPublishedMedia && (
                  <>
                    {/* Save: stage as draft (status 12), no validation. */}
                    <Button
                      type="outline-primary"
                      HTMLButtonType="button"
                      tooltipText="Save for later"
                      tooltipPlacement="top"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSave();
                      }}
                      isDisabled={disabled || actionsDisabled || noClipSelected}
                    >
                      Save
                    </Button>

                    {/* Publish: validate, then queue (status 0). */}
                    <Button
                      type="primary"
                      HTMLButtonType="button"
                      onClick={(e) => {
                        e.preventDefault();
                        // Fire analytics only on first-time publish, not on updates
                        if (formik.values.isNew === 100) {
                          trackEvent("upload", {
                            category: "distribution",
                            entity: platformType,
                            action: "publish",
                            location: isWizard ? "wizard" : "non-wizard",
                            ...getTrackUserContext(),
                          });
                        }
                        handlePublish();
                      }}
                      isDisabled={
                        disabled ||
                        actionsDisabled ||
                        isPublishingClips ||
                        noClipSelected
                      }
                    >
                      {/* In the media-detail sheet the action is always framed as
                            "Publish" (never Update/Republish) — the chip is queued
                            for publishing regardless of its create/update state.
                            Failed statuses (-1/-2) get "Retry" so the user knows
                            clicking re-attempts the same publish, not a first-time one. */}
                      {isPublishingClips
                        ? "Publishing..."
                        : effStatus === -1 || effStatus === -2
                          ? "Retry"
                          : inSheet || formik.values.isNew === 100
                            ? "Publish"
                            : ButtonText(formik.values.isNew || 0)}
                    </Button>
                  </>
                )}
              </div>
            </>
          );
          // In the media-detail sheet, pin the publishing toggle + actions to the bottom;
          // the sheet body scrolls behind them.
          return inSheet ? (
            <div className="socialSheet-actions">{footerContent}</div>
          ) : (
            footerContent
          );
        })()}
      <AlertDialog
        isOpen={showVisibilityDowngradeConfirm}
        onClose={() => setShowVisibilityDowngradeConfirm(false)}
        onConfirm={confirmVisibilityDowngradeAndPublish}
        title="ALERT!"
        message={`Are you sure you want to change the video visibility from Public to ${selectedVisibilityLabel}? This will hide the video from viewers on the platform.`}
        confirmText="Yes"
        cancelText="Cancel"
        confirmButtonType="danger"
      />
      {showPublishedDeleteConfirm && (
        <MediaDelete
          media={currentMediaDetails}
          isOpen={showPublishedDeleteConfirm}
          onClose={() => setShowPublishedDeleteConfirm(false)}
          hideSocials
          noteText="Do you really want to delete published video. It will remove video from social platform."
          onFormSubmit={async () => {
            setIsDeletingPublish(true);
            try {
              await handleCancelOrDelete(initialdata, "delete", platformType);
            } finally {
              setIsDeletingPublish(false);
            }
          }}
          // This card can render inside SocialPublish.tsx's Offcanvas sheet
          // (z-index 1060) - without this the confirm dialog would render
          // behind the sheet it was opened from.
          zIndex={1070}
        />
      )}
    </div>
  );
};

export default BasePublishCard;
